// Reproductor compartido de Digital Signage (concepto "EN VIVO").
// Dirigido por datos: recibe tema (tokens de color), escenas (playlist) y el
// catálogo, y pinta la secuencia en bucle (hero por producto ↔ menú a la carta).
// Lo usan tanto la vista previa interna (SignagePreview) como el reproductor
// público por token (SignageTV). El <div.sigp-stage> llena a su contenedor: el
// contenedor decide el tamaño (caja 16:9 en preview, viewport completo en TV).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Package } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { apiClient } from '@/lib/api/client';
import { cacheGet, cacheSet } from '@/features/pos/offline';
import { recortarFondoDataUri } from './shared';
import { CampaignOverlay } from './CampaignOverlay';
import type { CampanaInfo } from './CampaignOverlay';
import { LOCALE } from '@/lib/utils/regional';

export interface CatProducto {
  id: string; nombre: string; descripcion: string | null; marca: string | null;
  precio: number; iva_tarifa: number | null; controla_stock: boolean;
  stock: number | null; agotado: boolean; imagen: string | null;
  precio_antes?: number | null; descuento?: number | null; etiqueta?: string | null; etiqueta_color?: string | null;
  web_slug?: string | null;
}
export interface Categoria { categoria: string; n: number; productos: CatProducto[] }
export type TransKind = 'corte' | 'fundido' | 'deslizar' | 'zoom';
export const TRANSICIONES: { v: TransKind; l: string }[] = [
  { v: 'fundido', l: 'Fundido (crossfade)' },
  { v: 'deslizar', l: 'Deslizar' },
  { v: 'zoom', l: 'Zoom suave' },
  { v: 'corte', l: 'Corte (sin transición)' },
];
export interface Zona { rect: [number, number, number, number]; escenas: Escena[] }
export interface Escena { tipo: string; dur?: number; titulo?: string | null; texto?: string | null; url?: string | null; foco_x?: number; foco_y?: number; feed?: string; lat?: number; lon?: number; fecha?: string | null; fuente_id?: string; preset?: string; zonas?: Zona[]; trans?: TransKind; red?: string; clima?: string; combo_id?: string; productos?: string[]; marco?: string }
// Layouts compuestos (zonas simultáneas). rect = [x, y, w, h] en % de la pantalla.
export interface LayoutPreset { v: string; l: string; zonas: { rect: [number, number, number, number]; sugerido: string }[] }
export const LAYOUTS: LayoutPreset[] = [
  { v: 'lbar-der', l: 'Barra derecha 80/20 (estilo CNN: contenido + noticias)', zonas: [{ rect: [0, 0, 80, 100], sugerido: 'catalogo' }, { rect: [80, 0, 20, 100], sugerido: 'feed' }] },
  { v: 'lbar-izq', l: 'Barra izquierda 20/80', zonas: [{ rect: [0, 0, 20, 100], sugerido: 'feed' }, { rect: [20, 0, 80, 100], sugerido: 'catalogo' }] },
  { v: 'split-v', l: 'Mitades verticales 50/50', zonas: [{ rect: [0, 0, 50, 100], sugerido: 'catalogo' }, { rect: [50, 0, 50, 100], sugerido: 'imagen' }] },
  { v: 'split-h', l: 'Mitades horizontales (arriba/abajo)', zonas: [{ rect: [0, 0, 100, 50], sugerido: 'catalogo' }, { rect: [0, 50, 100, 50], sugerido: 'feed' }] },
];
export interface FeedFuente { id: string; nombre: string; items: { titulo: string; resumen?: string | null; publicado?: string | null }[] }
export interface TemaTokens { paper?: string; paper2?: string; ink?: string; soft?: string; accent?: string; sage?: string; oxido?: string; topo?: string }
export interface PlayerData {
  empresa?: string | null; local?: string | null;
  tema?: { preset?: string | null; tokens?: TemaTokens | null; logo?: string | null } | null;
  escenas?: Escena[]; categorias: Categoria[]; feeds?: FeedFuente[]; topVentas?: Categoria[]; lentos?: Categoria[]; combos?: Combo[]; cupones?: Cupon[]; porVencer?: VencItem[]; salud?: SaludData | null; social?: SocialCuenta[];
  ventasHoy?: Categoria[]; turnos?: TurnoMedico[]; lat?: number | null; lon?: number | null; resenas?: ResenasData | null;
  gadgets?: GadgetsCfg | null;
  avisos?: string[];   // avisos rápidos del local (419) — destacados en el ticker
  sync?: boolean;      // 423: video wall — posición derivada del reloj de pared
}
// Gadgets persistentes (config del LUGAR: pantalla ?? grupo). Clima/UV van al
// header; los chips (saludo/cuenta/local) a la capa GadgetsOverlay.
export interface GadgetItem {
  tipo: 'saludo' | 'cuenta' | 'local';
  pos: 'sup-centro' | 'inf-izq' | 'inf-der';
  texto?: string | null;      // local: texto libre (null → nombre del local)
  titulo?: string | null;     // cuenta: rótulo
  fecha?: string | null;      // cuenta: fecha manual
  combo_id?: string | null;   // cuenta: atada a la vigencia de un combo
}
export interface GadgetsCfg {
  activo?: boolean;
  clima?: boolean;            // header: temp + icono
  uv?: boolean;               // header: píldora UV (solo si UV≥6 y 10-16h)
  fecha?: boolean;            // header: fecha corta junto al reloj (off por defecto)
  ticker?: 'lenta' | 'normal' | 'rapida';   // velocidad de la cinta inferior (px/s constantes)
  items?: GadgetItem[];
  ocultar_en?: string[];      // kinds de slide donde la capa se esconde
  ocultar_patrocinado?: boolean;
}
export interface TurnoMedico { medico: string; especialidad?: string | null; proximo: string; cupos: number }
export interface ResenaItem { estrellas: number; comentario: string; autor: string; fecha: string }
export interface ResenasData { promedio: number; total: number; items: ResenaItem[] }
export interface SocialPost { texto?: string | null; imagen?: string | null; likes?: number | null; comentarios?: number | null; publicado_en?: string | null; fijado?: boolean }
export interface SocialCuenta { id: string; red: string; handle: string; nombre?: string | null; avatar?: string | null; posts: SocialPost[] }
export interface VencItem { id: string; nombre: string; marca?: string | null; dias: number; lote?: string | null; stock?: number | null; precio: number; precio_antes?: number | null; descuento: number; imagen?: string | null; imagen_ver?: string | null }
export interface SaludProd { id: string; nombre: string; marca?: string | null; precio: number; precio_antes?: number | null; descuento: number; imagen?: string | null; imagen_ver?: string | null }
export interface SaludTip { texto: string; icono?: string | null; condicion: string; cta?: string | null; producto?: SaludProd | null }
export interface SaludData { tips: SaludTip[]; lat?: number | null; lon?: number | null }
export interface Cupon { codigo: string; nombre: string; descripcion?: string | null; tipo: 'pct' | 'monto'; valor: number; producto?: string | null; hasta?: string | null; restantes?: number | null }
export interface ComboItem { id: string; nombre: string; marca?: string | null; cantidad: number; precio: number; imagen?: string | null; imagen_ver?: string | null }
export interface Combo { id: string; nombre: string; etiqueta?: string | null; descripcion?: string | null; descuento: number; imagen?: string | null; hasta?: string | null; items: ComboItem[]; suma: number; precio_pack: number; ahorro: number }
export interface SceneInfo { tipo: string; ref: string | null; titulo: string | null; dur: number }
export interface Anuncio { id: string; anunciante: string; tipo: 'banner' | 'mensaje'; media?: string | null; titulo?: string | null; texto?: string | null; url?: string | null; dur?: number; sov?: number }
export interface Phygital { activo?: boolean; modo?: 'producto' | 'tienda' | 'url'; cta?: string; url_base?: string | null; url_personalizada?: string | null; mostrar_en_producto?: boolean; utm?: string; tienda_slug?: string | null }

type Item = CatProducto & { categoria: string };
type Slide = ({ kind: 'hero'; item: Item; n: number; dur: number } | { kind: 'carta'; dur: number; titulo?: string | null }
  | { kind: 'msg'; dur: number; titulo?: string | null; texto?: string | null }
  | { kind: 'anuncio'; dur: number; ad: Anuncio }
  | { kind: 'qr'; dur: number; titulo?: string | null; texto?: string | null }
  | { kind: 'video'; dur: number; url: string; titulo?: string | null }
  | { kind: 'imagen'; dur: number; url: string; titulo?: string | null; foco_x?: number; foco_y?: number }
  | { kind: 'feed'; dur: number; feed: string; titulo?: string | null; lat?: number; lon?: number; fecha?: string | null; fuente_id?: string }
  | { kind: 'layout'; dur: number; zonas: Zona[] }
  | { kind: 'cross'; dur: number; titulo?: string | null }
  | { kind: 'combo'; dur: number; combo: Combo }
  | { kind: 'cupon'; dur: number; cupon: Cupon }
  | { kind: 'porvencer'; dur: number; titulo?: string | null; items: VencItem[] }
  | { kind: 'salud'; dur: number; titulo?: string | null }
  | { kind: 'social'; dur: number; post: SocialPost; cuenta: SocialCuenta }
  | { kind: 'turnos'; dur: number; titulo?: string | null; items: TurnoMedico[] }
  | { kind: 'resena'; dur: number; item: ResenaItem; promedio: number; total: number }
  | { kind: 'marco'; dur: number; n: number; item?: Item; url?: string | null; titulo?: string | null; texto?: string | null; color?: string | null }) & { trans?: TransKind };

const reduceMotion = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const DEFAULT_ESCENAS: Escena[] = [{ tipo: 'catalogo', dur: 9 }, { tipo: 'carta', dur: 16, titulo: 'La carta' }];
function hue(s: string): number { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360; return h; }

export const playerCss = `
.sigp-stage{ position:relative; width:100%; height:100%; overflow:hidden; container-type:size; container-name:sigp; user-select:none;
  color:var(--ink); background:var(--paper); font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
  --paper:#efe7da; --paper2:#e7ddcc; --ink:#191310; --soft:#6f655a; --accent:#c6303a; --sage:#4f7a52; --oxido:#b0552a; --topo:#8a8175; --dur:9s; }
.sigp-stage[data-preset="oscuro"]{ background:radial-gradient(1000px 520px at 78% -12%, color-mix(in srgb,var(--accent) 20%, transparent), transparent), var(--paper); }
.sigp-stage::after{ content:""; position:absolute; inset:0; pointer-events:none; opacity:.06; mix-blend-mode:multiply; z-index:6;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/></filter><rect width='120' height='120' filter='url(%23n)'/></svg>"); }
.sigp-stage[data-preset="oscuro"]::after{ mix-blend-mode:screen; opacity:.05; }
.sigp-progress{ position:absolute; top:0; left:0; height:.6cqh; background:var(--accent); z-index:7; width:0; animation:sigp-fill var(--dur) linear forwards; }
@keyframes sigp-fill{ from{width:0} to{width:100%} }
.sigp-top{ position:absolute; top:0; left:0; right:0; z-index:5; display:flex; align-items:center; justify-content:space-between; padding:2.4cqh 3.2cqw 0; }
.sigp-brand{ font-size:1.5cqw; font-weight:800; letter-spacing:.02em; } .sigp-brand small{ display:block; font-size:1cqw; font-weight:500; letter-spacing:.24em; text-transform:uppercase; color:var(--soft); margin-top:.3cqh; }
.sigp-logo{ height:3.6cqh; width:auto; max-width:20cqw; object-fit:contain; display:block; margin-bottom:.4cqh; }
.sigp-live{ display:flex; align-items:center; gap:1.2cqw; font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:1.15cqw; color:var(--soft); font-variant-numeric:tabular-nums; }
.sigp-live .idx{ color:var(--ink); font-weight:600; letter-spacing:.06em; } .sigp-live .sep{ opacity:.4; }
.sigp-dotlive{ width:.8cqw; height:.8cqw; border-radius:999px; background:var(--sage); box-shadow:0 0 0 .3cqw color-mix(in srgb,var(--sage) 30%,transparent); animation:sigp-beat 2.4s ease-in-out infinite; }
@keyframes sigp-beat{ 50%{ opacity:.45; transform:scale(.8); } }
.sigp-hero{ position:absolute; inset:0; display:grid; grid-template-columns:42% 58%; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-hero.flip{ grid-template-columns:58% 42%; } .sigp-hero.flip .sigp-photo{ order:-1; }
.sigp-photo{ position:relative; overflow:hidden; }
@keyframes sigp-ken{ from{ transform:scale(1) } to{ transform:scale(1.06) } }
/* Contrato de foto: fondo desenfocado + primer plano nítido (cover/contain), fade-in */
.sigp-ph-bg{ position:absolute; inset:-6%; background-size:cover; background-position:center; filter:blur(2.2cqw) saturate(1.12) brightness(.8); transform:scale(1.12); }
.sigp-stage[data-preset="oscuro"] .sigp-ph-bg{ filter:blur(2.2cqw) saturate(1.05) brightness(.55); }
.sigp-ph-fg{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity .6s ease; }
.sigp-ph-fg.on{ opacity:1; }
.sigp-ph-fg img{ width:100%; height:100%; }
.sigp-ph-fg.cover img{ object-fit:cover; animation:sigp-ken var(--dur) ease-out both; }
.sigp-ph-fg.contain img{ object-fit:contain; }
.sigp-ph-fallback{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; }
.sigp-photo::after{ content:""; position:absolute; inset:0; box-shadow:inset 0 0 12cqh 4cqh rgba(0,0,0,.16); pointer-events:none; }
.sigp-photo.out .sigp-ph-fg, .sigp-photo.out .sigp-ph-bg{ filter:grayscale(1); opacity:.5; }
.sigp-fallback{ width:24cqh; height:24cqh; opacity:.9; color:#fff; }
.sigp-txt{ position:relative; display:flex; flex-direction:column; justify-content:center; padding:0 3.4cqw; }
.sigp-txt::before{ content:""; position:absolute; top:14cqh; bottom:12cqh; width:1px; background:color-mix(in srgb,var(--ink) 22%,transparent); }
.sigp-hero:not(.flip) .sigp-txt::before{ right:0; } .sigp-hero.flip .sigp-txt::before{ left:0; }
.sigp-eyebrow{ font-size:1.15cqw; letter-spacing:.32em; text-transform:uppercase; color:var(--accent); font-weight:700; margin-bottom:1.4cqh; }
.sigp-name{ font-family:Georgia,"Times New Roman",serif; font-weight:600; font-size:6.2cqw; line-height:.98; letter-spacing:-.015em; text-wrap:balance; margin:0; overflow:hidden; animation:sigp-wipe .6s cubic-bezier(.7,0,.2,1) both; }
@keyframes sigp-wipe{ from{ clip-path:inset(0 0 100% 0); transform:translateY(.6cqh); } to{ clip-path:inset(0 0 0 0); transform:none; } }
.sigp-marca{ font-size:1cqw; letter-spacing:.22em; text-transform:uppercase; color:var(--soft); font-weight:600; margin-top:1cqh; }
.sigp-priceline{ display:flex; align-items:flex-end; gap:1cqw; margin-top:3cqh; font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-variant-numeric:tabular-nums; }
.sigp-cur{ font-size:3.2cqw; font-weight:600; margin-bottom:1.6cqh; color:var(--soft); }
.sigp-amt{ font-size:10.5cqw; font-weight:700; line-height:.8; letter-spacing:-.03em; }
.sigp-cents{ font-size:3.4cqw; font-weight:600; align-self:flex-start; margin-top:1cqh; }
.sigp-amt.struck{ text-decoration:line-through; text-decoration-thickness:.4cqh; color:var(--soft); }
.sigp-promo{ display:flex; flex-direction:column; gap:.4cqh; margin-bottom:1.6cqh; }
.sigp-antes{ font-size:1.7cqw; color:var(--soft); text-decoration:line-through; font-weight:600; }
.sigp-dcto{ align-self:flex-start; font-size:1.4cqw; font-weight:800; letter-spacing:.02em; color:#fff; background:var(--accent); padding:.2cqh .8cqw; border-radius:.6cqw; }
.sigp-etq{ position:absolute; top:2.4cqh; left:2.4cqw; z-index:3; font-size:1.2cqw; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:#fff; padding:.6cqh 1.1cqw; border-radius:.6cqw; box-shadow:0 2px 8px rgba(0,0,0,.35); }
.sigp-cetq{ font-size:.85cqw; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:#fff; padding:.15cqh .5cqw; border-radius:.4cqw; margin-left:.6cqw; }
.sigp-stock{ margin-top:2.4cqh; display:flex; align-items:center; gap:1cqw; font-size:1.15cqw; letter-spacing:.26em; text-transform:uppercase; font-weight:700; }
.sigp-stock .d{ width:.9cqw; height:.9cqw; border-radius:999px; }
.sigp-stock.ok{ color:var(--sage); } .sigp-stock.ok .d{ background:var(--sage); }
.sigp-stock.low{ color:var(--oxido); } .sigp-stock.low .d{ background:var(--oxido); animation:sigp-beat 1.8s ease-in-out infinite; }
.sigp-stock.out{ color:var(--topo); } .sigp-stock.out .d{ background:var(--topo); }
.sigp-meter{ display:inline-flex; gap:.5cqw; margin-left:.6cqw; } .sigp-meter i{ width:1.4cqw; height:.7cqh; background:var(--oxido); border-radius:1px; } .sigp-meter i.off{ background:color-mix(in srgb,var(--oxido) 22%,transparent); }
.sigp-carta{ position:absolute; inset:0; padding:9.4cqh 3.6cqw 7.4cqh; display:flex; flex-direction:column; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-carta h3{ font-family:Georgia,"Times New Roman",serif; font-weight:600; font-size:2.6cqw; letter-spacing:-.01em; margin:0 0 2cqh; }
.sigp-carta h3 em{ color:var(--soft); font-style:normal; font-size:1.4cqw; letter-spacing:.2em; text-transform:uppercase; }
.sigp-cols{ column-count:3; column-gap:3.4cqw; flex:1; min-height:0; }
.sigp-cat{ break-inside:avoid; margin-bottom:2.2cqh; }
.sigp-cat .lbl{ font-size:1.05cqw; letter-spacing:.28em; text-transform:uppercase; color:var(--accent); font-weight:700; padding-bottom:.5cqh; border-bottom:1px solid color-mix(in srgb,var(--ink) 22%,transparent); margin-bottom:.9cqh; }
.sigp-row{ display:flex; align-items:baseline; gap:.5cqw; margin:.6cqh 0; font-size:1.3cqw; }
.sigp-row .n{ color:var(--ink); } .sigp-row .lead{ flex:1; border-bottom:1px dotted color-mix(in srgb,var(--ink) 40%,transparent); transform:translateY(-.35cqh); }
.sigp-row .p{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-variant-numeric:tabular-nums; font-weight:600; }
.sigp-row.out .n{ color:var(--topo); } .sigp-row.out .p{ color:var(--topo); text-decoration:line-through; }
.sigp-msg{ position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:8cqh 8cqw; gap:2cqh; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-msg h2{ font-family:Georgia,"Times New Roman",serif; font-weight:600; font-size:5cqw; margin:0; text-wrap:balance; }
.sigp-msg p{ font-size:2cqw; color:var(--soft); margin:0; max-width:60cqw; }
/* Retail media — anuncio a pantalla completa (takeover sobre header/ticker) */
.sigp-ad{ position:absolute; inset:0; z-index:8; display:flex; align-items:center; justify-content:center; background:var(--paper); animation:sigp-wipe .5s cubic-bezier(.7,0,.2,1) both; overflow:hidden; }
.sigp-ad.banner{ background:#000; }
.sigp-adimg{ width:100%; height:100%; object-fit:cover; }
.sigp-admsg{ text-align:center; padding:8cqh 9cqw; display:flex; flex-direction:column; gap:2cqh; align-items:center; }
.sigp-admsg .who{ font-size:1.3cqw; letter-spacing:.28em; text-transform:uppercase; color:var(--accent); font-weight:800; }
.sigp-admsg h2{ font-family:Georgia,"Times New Roman",serif; font-weight:600; font-size:5.2cqw; margin:0; text-wrap:balance; color:var(--ink); }
.sigp-admsg p{ font-size:2cqw; color:var(--soft); margin:0; max-width:64cqw; }
.sigp-adtag{ position:absolute; bottom:2.6cqh; right:2.4cqw; z-index:9; font-size:1cqw; letter-spacing:.24em; text-transform:uppercase; font-weight:700;
  color:var(--paper); background:color-mix(in srgb,var(--ink) 78%,transparent); padding:.6cqh 1.2cqw; border-radius:.5cqw; backdrop-filter:blur(4px); }
.sigp-ad.banner .sigp-adtag{ background:rgba(0,0,0,.55); color:#fff; }
/* QR phygital — escena grande (pantalla → teléfono → tienda) */
.sigp-qr{ position:absolute; inset:0; z-index:2; display:flex; align-items:center; justify-content:center; gap:6cqw; padding:8cqh 8cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-qrtxt{ flex:1; max-width:46cqw; }
.sigp-qrtxt .sigp-eyebrow{ color:var(--accent); font-size:1.4cqw; letter-spacing:.28em; text-transform:uppercase; font-weight:800; }
.sigp-qrtxt h2{ font-family:Georgia,"Times New Roman",serif; font-weight:600; font-size:4.6cqw; margin:.6cqh 0; text-wrap:balance; color:var(--ink); }
.sigp-qrtxt p{ font-size:2cqw; color:var(--soft); margin:0; max-width:40cqw; }
.sigp-qrbox{ background:#fff; padding:2.4cqh 2.2cqw; border-radius:1.4cqw; box-shadow:0 12px 40px rgba(0,0,0,.28); display:flex; align-items:center; justify-content:center; }
.sigp-qrbox svg{ width:26cqh; height:26cqh; display:block; }
.sigp-qrempty{ width:26cqh; height:26cqh; display:flex; align-items:center; justify-content:center; color:#bbb; }
/* Escena de imagen a pantalla completa (biblioteca de medios, con punto focal) */
/* Cross-sell en tiempo real: panel "también te puede interesar" */
.sigp-cross{ position:absolute; inset:0; z-index:2; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4cqh; padding:9cqh 6cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-cross-tit{ font-family:Georgia,"Times New Roman",serif; font-weight:600; font-size:4.2cqw; color:var(--ink); text-wrap:balance; }
.sigp-cross-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:2.4cqw 2cqw; width:100%; max-width:88cqw; }
.sigp-cross-card{ border:1px solid color-mix(in srgb,var(--ink) 14%,transparent); border-radius:1cqw; padding:2.2cqh 1.6cqw; background:color-mix(in srgb,var(--paper2) 60%,transparent); display:flex; flex-direction:column; gap:.6cqh; }
.sigp-cross-card .n{ font-size:1.7cqw; font-weight:600; line-height:1.15; }
.sigp-cross-card .m{ font-size:1cqw; letter-spacing:.16em; text-transform:uppercase; color:var(--soft); }
.sigp-cross-card .p{ margin-top:auto; font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-weight:700; font-size:2.4cqw; color:var(--accent); display:flex; align-items:baseline; gap:.8cqw; }
.sigp-cross-card .p .d{ font-size:1.1cqw; font-weight:800; color:#fff; background:var(--accent); padding:.1cqh .6cqw; border-radius:.4cqw; }
/* Gadgets persistentes: clima/UV en el header + chips en capa propia */
.sigg-wx{ display:inline-flex; align-items:baseline; gap:.5cqw; } .sigg-wx em{ font-style:normal; font-size:1.3cqw; }
.sigg-uv{ color:var(--paper); background:var(--accent); font-weight:800; font-size:.95cqw; letter-spacing:.08em; padding:.35cqh .8cqw; border-radius:2cqw; }
.sigg-layer{ position:absolute; inset:0; z-index:6; pointer-events:none; }
.sigg-slot{ position:absolute; display:flex; gap:1cqh; }
.sigg-sup-centro{ top:9.5cqh; left:50%; transform:translateX(-50%); flex-direction:row; }
.sigg-inf-izq{ bottom:8.6cqh; left:2.4cqw; flex-direction:column; align-items:flex-start; }
.sigg-inf-der{ bottom:8.6cqh; right:2.4cqw; flex-direction:column-reverse; align-items:flex-end; } /* apila hacia arriba: no tapa el mini-QR */
.sigg-chip{ display:inline-flex; align-items:baseline; gap:.8cqw; max-width:30cqw;
  background:color-mix(in srgb, var(--paper) 78%, transparent); backdrop-filter:blur(6px);
  border:1px solid color-mix(in srgb, var(--ink) 14%, transparent); border-radius:2.4cqw;
  padding:.9cqh 1.4cqw; font-size:1.2cqw; color:var(--ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sigg-chip b{ font-weight:700; } .sigg-chip .num{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-variant-numeric:tabular-nums; color:var(--accent); font-weight:700; }
.sigg-saludo{ font-family:Georgia,"Times New Roman",serif; font-size:1.4cqw; }

/* Reseñas internas */
.sigp-resena{ position:absolute; inset:0; z-index:2; display:flex; flex-direction:column; justify-content:center; align-items:center; text-align:center; gap:4cqh; padding:8cqh 12cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-resena-head{ display:flex; align-items:baseline; gap:1.6cqw; }
.sigp-resena-head .prom{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:6cqw; font-weight:700; font-variant-numeric:tabular-nums; }
.sigp-resena-head .stars{ font-size:3.4cqw; color:var(--accent); letter-spacing:.1em; }
.sigp-resena-head .tot{ font-size:1.5cqw; color:var(--soft); }
.sigp-resena-quote{ margin:0; max-width:70cqw; }
.sigp-resena-quote .qstars{ display:block; font-size:2cqw; color:var(--accent); letter-spacing:.14em; margin-bottom:1.6cqh; }
.sigp-resena-quote p{ margin:0; font-family:Georgia,"Times New Roman",serif; font-size:3.4cqw; line-height:1.35; text-wrap:balance; }
.sigp-resena-quote footer{ margin-top:2.2cqh; font-size:1.6cqw; color:var(--soft); }

/* Turnos: médicos disponibles hoy */
.sigp-turnos{ position:absolute; inset:0; z-index:2; display:flex; flex-direction:column; justify-content:center; gap:1.6cqh; padding:7cqh 9cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-turnos-tit{ font-family:Georgia,"Times New Roman",serif; font-weight:700; font-size:5.2cqw; line-height:1.02; text-wrap:balance; }
.sigp-turnos-sub{ font-size:1.7cqw; color:var(--soft); margin-bottom:1.4cqh; }
.sigp-turnos-lista{ list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:1.8cqh; }
.sigp-turnos-lista li{ display:flex; align-items:center; gap:2cqw; border-bottom:1px solid color-mix(in srgb,var(--ink) 12%,transparent); padding-bottom:1.6cqh; }
.sigp-turnos-lista .quien{ flex:1; min-width:0; }
.sigp-turnos-lista .quien b{ display:block; font-size:2.4cqw; line-height:1.15; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sigp-turnos-lista .quien em{ font-style:normal; font-size:1.4cqw; color:var(--soft); letter-spacing:.08em; text-transform:uppercase; }
.sigp-turnos-lista .cuando{ display:flex; align-items:baseline; gap:1.4cqw; flex:none; }
.sigp-turnos-lista .prox{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:3.2cqw; font-weight:700; color:var(--accent); font-variant-numeric:tabular-nums; }
.sigp-turnos-lista .cupos{ font-size:1.4cqw; color:var(--paper); background:var(--sage); font-weight:700; padding:.5cqh 1.2cqw; border-radius:2cqw; white-space:nowrap; }

/* Redes sociales: tarjeta de post */
.sigp-social{ position:absolute; inset:0; z-index:2; display:flex; align-items:center; justify-content:center; padding:7cqh 6cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-social-card{ display:grid; grid-template-columns:1fr 1fr; width:min(74cqw,150cqh); max-height:78cqh; background:var(--paper2); border:1px solid color-mix(in srgb,var(--ink) 10%,transparent); border-radius:2cqw; overflow:hidden; box-shadow:0 2.4cqh 7cqh color-mix(in srgb,var(--ink) 22%,transparent); }
.sigp-social-card.solo-texto{ grid-template-columns:1fr; width:min(58cqw,120cqh); }
.sigp-social-card .img{ position:relative; min-height:46cqh; background:color-mix(in srgb,var(--ink) 8%,var(--paper2)); }
.sigp-social-card .img img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
.sigp-social-card .body{ display:flex; flex-direction:column; gap:2cqh; padding:3.4cqh 2.6cqw; min-width:0; }
.sigp-social-card .head{ display:flex; align-items:center; gap:1.2cqw; }
.sigp-social-card .ava{ width:3.6cqw; height:3.6cqw; border-radius:50%; object-fit:cover; flex:none; }
.sigp-social-card .ava.ini{ display:flex; align-items:center; justify-content:center; color:#fff; font-weight:800; font-size:1.7cqw; }
.sigp-social-card .who{ min-width:0; flex:1; display:flex; flex-direction:column; }
.sigp-social-card .who b{ font-size:1.6cqw; line-height:1.15; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sigp-social-card .who span{ font-size:1.15cqw; color:var(--soft); }
.sigp-social-card .net{ flex:none; color:#fff; font-size:1.05cqw; font-weight:800; letter-spacing:.08em; text-transform:uppercase; padding:.55cqh 1.1cqw; border-radius:2cqw; }
.sigp-social-card .txt{ margin:0; font-size:1.9cqw; line-height:1.4; display:-webkit-box; -webkit-line-clamp:7; -webkit-box-orient:vertical; overflow:hidden; }
.sigp-social-card.solo-texto .txt{ font-family:Georgia,"Times New Roman",serif; font-size:2.6cqw; line-height:1.35; -webkit-line-clamp:8; }
.sigp-social-card .foot{ margin-top:auto; display:flex; align-items:center; gap:1.8cqw; font-size:1.3cqw; color:var(--soft); font-variant-numeric:tabular-nums; }
.sigp-social-card .foot .f{ margin-left:auto; }
.sigp-social-card .foot .cta{ color:var(--paper); background:var(--accent); font-weight:800; font-size:1.05cqw; letter-spacing:.1em; text-transform:uppercase; padding:.6cqh 1.3cqw; border-radius:2cqw; }

/* Combos / kits de temporada */
.sigp-combo{ position:absolute; inset:0; z-index:2; display:flex; flex-direction:column; justify-content:center; gap:3.5cqh; padding:6cqh 7cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-combo-head{ text-align:center; }
.sigp-combo-etq{ display:inline-block; font-size:1.2cqw; letter-spacing:.24em; text-transform:uppercase; font-weight:800; color:var(--paper); background:var(--accent); padding:.6cqh 1.6cqw; border-radius:2cqw; }
.sigp-combo-name{ font-family:Georgia,"Times New Roman",serif; font-weight:700; font-size:6cqw; line-height:1.02; margin:1cqh 0 0; text-wrap:balance; }
.sigp-combo-desc{ font-size:1.7cqw; color:var(--soft); margin:.6cqh 0 0; }
.sigp-combo-body{ display:grid; grid-template-columns:1.5fr 1fr; gap:5cqw; align-items:center; }
.sigp-combo-items{ list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:1.6cqh; }
.sigp-combo-items li{ display:flex; align-items:center; gap:1.8cqw; border-bottom:1px solid color-mix(in srgb,var(--ink) 12%,transparent); padding-bottom:1.4cqh; }
.sigp-combo-items img{ width:5.4cqw; height:5.4cqw; object-fit:cover; border-radius:1cqw; background:var(--paper2); flex:none; }
.sigp-combo-items .ph{ width:5.4cqw; height:5.4cqw; border-radius:1cqw; background:color-mix(in srgb,var(--paper2) 70%,transparent); display:flex; align-items:center; justify-content:center; color:var(--soft); flex:none; }
.sigp-combo-items .ph svg{ width:2.6cqw; height:2.6cqw; }
.sigp-combo-items .n{ flex:1; font-size:2.1cqw; font-weight:600; line-height:1.15; }
.sigp-combo-items .n em{ font-style:normal; color:var(--soft); font-size:1.3cqw; letter-spacing:.06em; text-transform:uppercase; }
.sigp-combo-items .pr{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:1.9cqw; color:var(--soft); font-variant-numeric:tabular-nums; }
.sigp-combo-price{ display:flex; flex-direction:column; align-items:center; gap:1.2cqh; text-align:center; }
.sigp-combo-price .ahorro{ font-size:1.7cqw; font-weight:800; color:var(--paper); background:var(--accent); padding:.7cqh 1.8cqw; border-radius:2cqw; }
.sigp-combo-price .antes{ font-size:1.8cqw; color:var(--soft); text-decoration:line-through; }
.sigp-combo-price .pack{ display:flex; flex-direction:column; gap:.4cqh; }
.sigp-combo-price .pack span{ font-size:1.3cqw; letter-spacing:.22em; text-transform:uppercase; color:var(--soft); }
.sigp-combo-price .pack b{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:8cqw; font-weight:800; color:var(--accent); line-height:1; font-variant-numeric:tabular-nums; }
.sigp-cupon{ position:absolute; inset:0; z-index:2; display:flex; align-items:center; justify-content:center; padding:8cqh 8cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-cupon-card{ border:.4cqw dashed var(--accent); border-radius:2.2cqw; padding:5cqh 6cqw; text-align:center; max-width:74cqw;
  background:color-mix(in srgb,var(--paper2) 55%,transparent); box-shadow:0 2cqh 6cqh color-mix(in srgb,var(--ink) 10%,transparent); }
.sigp-cupon-eyebrow{ font-size:1.25cqw; letter-spacing:.26em; text-transform:uppercase; color:var(--soft); font-weight:700; }
.sigp-cupon-valor{ font-family:Georgia,"Times New Roman",serif; font-size:8.5cqw; font-weight:800; color:var(--accent); line-height:1; margin-top:1.2cqh; }
.sigp-cupon-name{ font-family:Georgia,"Times New Roman",serif; font-size:3.4cqw; font-weight:700; margin:1cqh 0 0; line-height:1.05; text-wrap:balance; }
.sigp-cupon-desc{ font-size:1.7cqw; color:var(--soft); margin:.6cqh 0 0; }
.sigp-cupon-code{ display:inline-block; font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:6.5cqw; font-weight:800;
  letter-spacing:.14em; background:var(--ink); color:var(--paper); padding:1.4cqh 3.4cqw; border-radius:1.6cqw; margin-top:2.4cqh; }
.sigp-cupon-meta{ display:flex; justify-content:center; gap:2.4cqw; flex-wrap:wrap; margin-top:2.2cqh; font-size:1.5cqw; color:var(--soft); }
.sigp-cupon-meta .urge{ color:var(--accent); font-weight:700; }
/* Por vencer / liquidación anti-merma */
.sigp-venc{ position:absolute; inset:0; z-index:2; display:flex; flex-direction:column; gap:3cqh; padding:6cqh 5cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-venc-tit{ text-align:center; font-family:Georgia,"Times New Roman",serif; font-weight:700; font-size:4.2cqw; text-wrap:balance; }
.sigp-venc-tit .bang{ color:#fff; background:#d4342c; padding:.3cqh 1.4cqw; border-radius:2cqw; font-family:inherit; }
.sigp-venc-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:2cqh 2cqw; width:100%; }
.sigp-venc-card{ display:flex; gap:1.4cqw; align-items:center; border:1px solid color-mix(in srgb,var(--ink) 14%,transparent); border-left:.6cqw solid var(--accent); border-radius:1cqw; padding:1.6cqh 1.4cqw; background:color-mix(in srgb,var(--paper2) 55%,transparent); }
.sigp-venc-card.med{ border-left-color:#e8a13a; } .sigp-venc-card.urg{ border-left-color:#d4342c; background:color-mix(in srgb,#d4342c 8%,transparent); }
.sigp-venc-card img{ width:6cqh; height:6cqh; object-fit:cover; border-radius:.8cqw; background:var(--paper2); flex:none; }
.sigp-venc-card .ph{ width:6cqh; height:6cqh; border-radius:.8cqw; background:color-mix(in srgb,var(--paper2) 70%,transparent); display:flex; align-items:center; justify-content:center; color:var(--soft); flex:none; }
.sigp-venc-card .ph svg{ width:2.6cqh; height:2.6cqh; }
.sigp-venc-card .body{ flex:1; min-width:0; display:flex; flex-direction:column; gap:.4cqh; }
.sigp-venc-card .n{ font-size:1.5cqw; font-weight:600; line-height:1.1; }
.sigp-venc-card .n em{ font-style:normal; color:var(--soft); font-size:1cqw; text-transform:uppercase; letter-spacing:.05em; }
.sigp-venc-card .vence{ font-size:1.1cqw; font-weight:700; letter-spacing:.02em; color:#e8a13a; }
.sigp-venc-card .vence.urg{ color:#d4342c; } .sigp-venc-card .vence.med{ color:#e8a13a; }
.sigp-venc-card .p{ display:flex; align-items:baseline; gap:.8cqw; font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-variant-numeric:tabular-nums; }
.sigp-venc-card .p .antes{ font-size:1.1cqw; color:var(--soft); text-decoration:line-through; }
.sigp-venc-card .p .now{ font-size:2.1cqw; font-weight:800; color:var(--accent); }
.sigp-venc-card .p .d{ font-size:1cqw; font-weight:800; color:#fff; background:#d4342c; padding:.1cqh .6cqw; border-radius:.4cqw; }
/* Salud del día (consejo + clima/UV + producto sugerido) */
.sigp-salud{ position:absolute; inset:0; z-index:2; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:3.2cqh; padding:7cqh 8cqw; text-align:center; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-salud-tit{ font-size:1.6cqw; letter-spacing:.26em; text-transform:uppercase; color:var(--accent); font-weight:800; }
.sigp-salud-tip{ display:flex; align-items:center; gap:2.5cqw; max-width:84cqw; }
.sigp-salud-tip .ic{ font-size:11cqw; line-height:1; flex:none; }
.sigp-salud-tip p{ margin:0; font-family:Georgia,"Times New Roman",serif; font-weight:600; font-size:4.4cqw; line-height:1.12; text-wrap:balance; text-align:left; }
.sigp-salud-clima{ display:flex; align-items:center; gap:1.6cqw; font-family:"SF Mono","Cascadia Mono",Consolas,monospace; }
.sigp-salud-clima .wic{ font-size:3.4cqw; } .sigp-salud-clima .t{ font-size:3cqw; font-weight:700; }
.sigp-salud-clima .uv{ font-size:1.5cqw; font-weight:800; color:var(--paper); background:var(--accent); padding:.3cqh 1cqw; border-radius:1cqw; }
.sigp-salud-clima .lbl{ font-size:1.5cqw; color:var(--soft); text-transform:capitalize; font-family:Georgia,serif; }
.sigp-salud-prod{ display:flex; align-items:center; gap:1.8cqw; border:1px solid color-mix(in srgb,var(--ink) 14%,transparent); border-left:.6cqw solid var(--accent); border-radius:1cqw; padding:1.8cqh 2cqw; background:color-mix(in srgb,var(--paper2) 55%,transparent); }
.sigp-salud-prod img, .sigp-salud-prod .ph{ width:8cqh; height:8cqh; object-fit:cover; border-radius:.9cqw; background:var(--paper2); flex:none; display:flex; align-items:center; justify-content:center; color:var(--soft); }
.sigp-salud-prod .ph svg{ width:3cqh; height:3cqh; }
.sigp-salud-prod .body{ text-align:left; display:flex; flex-direction:column; gap:.4cqh; }
.sigp-salud-prod .cta{ font-size:1.1cqw; letter-spacing:.18em; text-transform:uppercase; color:var(--accent); font-weight:800; }
.sigp-salud-prod .n{ font-size:2.2cqw; font-weight:600; line-height:1.1; } .sigp-salud-prod .n em{ font-style:normal; color:var(--soft); font-size:1.3cqw; text-transform:uppercase; }
.sigp-salud-prod .p{ display:flex; align-items:baseline; gap:.9cqw; font-family:"SF Mono","Cascadia Mono",Consolas,monospace; }
.sigp-salud-prod .p .antes{ font-size:1.4cqw; color:var(--soft); text-decoration:line-through; }
.sigp-salud-prod .p .now{ font-size:2.6cqw; font-weight:800; color:var(--accent); }
.sigp-salud-prod .p .d{ font-size:1.1cqw; font-weight:800; color:#fff; background:var(--accent); padding:.1cqh .6cqw; border-radius:.4cqw; }
/* Layout compuesto (zonas simultáneas). Cada zona es su propio contenedor (container-query) */
.sigp-layout{ position:absolute; inset:0; z-index:2; background:var(--paper); }
.sigp-zona{ position:absolute; overflow:hidden; outline:1px solid color-mix(in srgb,var(--ink) 8%,transparent); outline-offset:-1px; }
.sigp-patro{ position:absolute; top:2.2cqh; left:50%; transform:translateX(-50%); z-index:9; font-size:1cqw; letter-spacing:.22em; text-transform:uppercase; font-weight:700; color:var(--paper); background:color-mix(in srgb,var(--ink) 78%,transparent); padding:.5cqh 1.1cqw; border-radius:.5cqw; backdrop-filter:blur(4px); }
.sigp-imgscene{ position:absolute; inset:0; background:#000; }
.sigp-imgscene img{ width:100%; height:100%; object-fit:cover; display:block; animation:sigp-ken var(--dur) ease-out both; }
/* Feeds externos (reloj / clima / cuenta regresiva) */
.sigp-feed{ position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; gap:2cqh; padding:8cqh 8cqw; animation:sigp-wipe .55s cubic-bezier(.7,0,.2,1) both; }
.sigp-feed-tit{ font-size:1.5cqw; letter-spacing:.28em; text-transform:uppercase; color:var(--accent); font-weight:800; }
.sigp-feed-hora{ font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-weight:700; font-size:20cqw; line-height:.9; letter-spacing:-.02em; font-variant-numeric:tabular-nums; }
.sigp-feed-fecha{ font-size:2.4cqw; color:var(--soft); text-transform:capitalize; }
.sigp-clima-main{ display:flex; align-items:center; gap:2.5cqw; }
.sigp-clima-main .ic{ font-size:12cqw; line-height:1; }
.sigp-clima-main .t{ font-family:Georgia,"Times New Roman",serif; font-weight:600; font-size:14cqw; line-height:1; }
.sigp-feed-cd{ display:flex; gap:3.5cqw; }
.sigp-feed-cd span{ display:flex; flex-direction:column; gap:.6cqh; font-size:1.5cqw; text-transform:uppercase; letter-spacing:.1em; color:var(--soft); }
.sigp-feed-cd b{ font-family:Georgia,"Times New Roman",serif; font-size:9cqw; color:var(--ink); font-weight:700; line-height:1; font-variant-numeric:tabular-nums; }
.sigp-feed.noticias{ justify-content:flex-start; padding:9cqh 7cqw; gap:3cqh; }
.sigp-noticias{ list-style:none; margin:0; padding:0; width:100%; max-width:82cqw; display:flex; flex-direction:column; gap:2cqh; text-align:left; }
.sigp-noticias li{ display:flex; align-items:flex-start; gap:1.4cqw; font-family:Georgia,"Times New Roman",serif; font-size:2.8cqw; line-height:1.2; color:var(--ink); border-bottom:1px solid color-mix(in srgb,var(--ink) 14%,transparent); padding-bottom:1.4cqh; }
.sigp-noticias li .b{ flex:none; width:1.1cqw; height:1.1cqw; margin-top:1cqh; border-radius:999px; background:var(--accent); }
/* Escena de video (archivo o embed) a pantalla completa */
.sigp-video{ position:absolute; inset:0; background:#000; display:flex; align-items:center; justify-content:center; }
.sigp-video video, .sigp-video iframe{ width:100%; height:100%; border:0; object-fit:cover; display:block; }
.sigp-vcap{ position:absolute; left:3cqw; bottom:8.5cqh; z-index:2; color:#fff; font-family:Georgia,"Times New Roman",serif; font-size:3cqw; font-weight:600; text-wrap:balance; max-width:60cqw; text-shadow:0 2px 14px rgba(0,0,0,.65); }
/* Mini-QR "compra este producto" sobre el hero */
.sigp-heroqr{ position:absolute; bottom:2.4cqh; right:2.4cqw; z-index:4; background:rgba(255,255,255,.96); padding:1cqh 1cqw .8cqh; border-radius:.9cqw; box-shadow:0 6px 20px rgba(0,0,0,.3); display:flex; flex-direction:column; align-items:center; gap:.5cqh; max-width:13cqw; }
.sigp-heroqr svg{ width:8cqh; height:8cqh; display:block; }
.sigp-heroqr span{ font-size:.85cqw; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:#101010; text-align:center; line-height:1.15; }
.sigp-ticker{ position:absolute; bottom:0; left:0; right:0; height:6.2cqh; z-index:5; display:flex; align-items:center; overflow:hidden;
  background:color-mix(in srgb,var(--ink) 92%,var(--accent)); color:var(--paper); font-family:"SF Mono","Cascadia Mono",Consolas,monospace; font-size:1.35cqw; font-variant-numeric:tabular-nums; }
.sigp-stage[data-preset="oscuro"] .sigp-ticker{ background:color-mix(in srgb,#000 45%,var(--paper2)); color:var(--ink); border-top:1px solid color-mix(in srgb,var(--accent) 35%,transparent); }
.sigp-run{ display:inline-flex; white-space:nowrap; padding-left:100%; animation:sigp-scroll 40s linear infinite; }
.sigp-it{ padding:0 2.4cqw; display:inline-flex; gap:1cqw; align-items:center; } .sigp-it b{ font-weight:400; opacity:.7; } .sigp-it s{ text-decoration:none; color:var(--accent); font-weight:700; filter:brightness(1.7); }
.sigp-avi{ padding:.3cqh 2cqw; margin:0 .8cqw; display:inline-flex; align-items:center; gap:.8cqw; background:var(--accent); color:var(--paper); border-radius:2cqw; font-weight:700; }
.sigp-tsep{ opacity:.3; } @keyframes sigp-scroll{ to{ transform:translateX(-50%) } }
.sigp-empty{ position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:1.5cqh; color:var(--soft); }
/* ===== Escena MARCO ROTO (pop-out "3D": el producto se sale del marco) =====
   Capas: fondo interior (z1) → marco negro dibujado (z2) → producto sin clip (z3).
   La "salida" es un clip-path que arranca en la boca del marco y se abre; la
   sombra sobre el plano del marco + la levitación venden la profundidad. */
.sigp-hero.sigm{ grid-template-columns:52% 48%; } .sigp-hero.sigm.flip{ grid-template-columns:48% 52%; }
.sigm-side{ position:relative; overflow:hidden; }
.sigm-fondo{ position:absolute; inset:15% 12% 17% 12%; z-index:1; border-radius:.5cqw; overflow:hidden;
  background:radial-gradient(62% 55% at 50% 40%, color-mix(in srgb,#fff 46%,transparent), transparent 72%),
             linear-gradient(160deg, var(--paper2), color-mix(in srgb,var(--accent) 16%, var(--paper2))); }
.sigm-fondo::after{ content:""; position:absolute; inset:0; box-shadow:inset 0 0 7cqh 1cqh rgba(0,0,0,.16); }
.sigp-stage[data-preset="oscuro"] .sigm-fondo{
  background:radial-gradient(62% 55% at 50% 40%, color-mix(in srgb,var(--accent) 26%,transparent), transparent 72%),
             linear-gradient(160deg, color-mix(in srgb,#fff 7%,var(--paper2)), var(--paper2)); }
.sigm-frame{ position:absolute; inset:15% 12% 17% 12%; z-index:2; pointer-events:none; border:1.1cqw solid #17110b; border-radius:.6cqw;
  box-shadow:0 1.4cqh 4.5cqh rgba(0,0,0,.30), inset 0 0 0 .16cqw rgba(255,255,255,.30); }
.sigm-shadow{ position:absolute; left:50%; bottom:19%; width:42%; height:5.5cqh; transform:translateX(-50%); z-index:2;
  background:radial-gradient(closest-side, rgba(0,0,0,.36), transparent); filter:blur(.6cqh);
  animation:sigm-sombra 5.2s ease-in-out 3.4s infinite alternate; }
@keyframes sigm-sombra{ from{ transform:translateX(-50%) scale(1); opacity:.9; } to{ transform:translateX(-50%) scale(.8); opacity:.5; } }
/* Salida LENTA y dramática: 0.8s de espera (el marco se "lee" primero) + 2.5s de
   emergencia; la levitación y la sombra respiran recién cuando terminó de salir. */
.sigm-pop{ position:absolute; inset:4% 3% 9% 3%; z-index:3; display:flex; align-items:flex-end; justify-content:center;
  animation:sigm-salir 2.5s cubic-bezier(.22,.7,.25,1.03) .8s both; }
@keyframes sigm-salir{
  from{ clip-path:inset(15% 13% 10% 13%); transform:scale(.8) translateY(1.6cqh); }
  55%{ clip-path:inset(0 0 0 0); }
  to{ clip-path:inset(0 0 0 0); transform:scale(1) translateY(0); } }
.sigm-pop img{ max-width:80%; max-height:100%; object-fit:contain; transform-origin:50% 88%;
  filter:drop-shadow(1cqw 2cqh 1.3cqw rgba(0,0,0,.32));
  animation:sigm-flotar 5.2s ease-in-out 3.4s infinite alternate; }
@keyframes sigm-flotar{ from{ transform:translateY(0) rotate(-1.1deg); } to{ transform:translateY(-2cqh) rotate(1.1deg); } }
.sigm-pop.out{ filter:grayscale(1); opacity:.55; }
/* Variante digna sin recorte (foto ambientada): la foto vive DENTRO del marco */
.sigm-flat{ position:absolute; inset:15% 12% 17% 12%; z-index:1; overflow:hidden; border-radius:.5cqw; display:flex; align-items:center; justify-content:center; }
.sigm-flat img{ width:100%; height:100%; object-fit:cover; animation:sigp-ken var(--dur) ease-out both; }
.sigm-flat.out{ filter:grayscale(1); opacity:.55; }
.sigm-vacio{ width:16cqh; height:16cqh; color:var(--topo); opacity:.7; }
.sigm-texto{ font-size:1.8cqw; color:var(--soft); margin:2cqh 0 0; max-width:34cqw; }
.sigm-side .sigp-etq{ z-index:4; }
/* ===== ORIENTACIÓN VERTICAL 9:16 — reflow por container-query (auto en pantallas retrato) ===== */
@container sigp (max-aspect-ratio: 3/4){
  .sigp-top{ padding:2.4cqh 5cqw 0; }
  .sigp-brand{ font-size:2.6cqw; } .sigp-brand small{ font-size:1.7cqw; }
  .sigp-logo{ height:5cqh; max-width:40cqw; }
  .sigp-live{ font-size:2cqw; gap:2cqw; } .sigp-dotlive{ width:1.4cqw; height:1.4cqw; }
  /* Salud del día: tipografía mayor en vertical */
  .sigp-salud-tip p{ font-size:6.4cqw; } .sigp-salud-tip .ic{ font-size:16cqw; }
  .sigp-salud-prod .n{ font-size:3.6cqw; } .sigp-salud-prod .p .now{ font-size:4.2cqw; }
  /* Por vencer: una columna en vertical, tipografía mayor */
  .sigp-venc-grid{ grid-template-columns:1fr; }
  .sigp-venc-tit{ font-size:6cqw; } .sigp-venc-card .n{ font-size:3cqw; } .sigp-venc-card .vence{ font-size:2.2cqw; }
  .sigp-venc-card .p .now{ font-size:3.6cqw; } .sigp-venc-card .p .antes{ font-size:2cqw; } .sigp-venc-card .p .d{ font-size:2cqw; }
  .sigp-venc-card img, .sigp-venc-card .ph{ width:10cqh; height:10cqh; }
  /* Gadgets: en vertical el header colapsa el índice; chips más grandes */
  .sigg-wx em{ font-size:2.2cqw; } .sigg-uv{ font-size:1.8cqw; }
  .sigg-chip{ font-size:2.4cqw; max-width:60cqw; padding:1cqh 2.6cqw; border-radius:5cqw; }
  .sigg-saludo{ font-size:2.8cqw; }
  .sigg-sup-centro{ top:8cqh; } .sigg-inf-izq{ bottom:7.5cqh; left:4cqw; } .sigg-inf-der{ bottom:7.5cqh; right:4cqw; }
  /* Reseñas: tipografía mayor en vertical */
  .sigp-resena-head .prom{ font-size:12cqw; } .sigp-resena-head .stars{ font-size:6cqw; } .sigp-resena-head .tot{ font-size:3cqw; }
  .sigp-resena-quote{ max-width:88cqw; } .sigp-resena-quote p{ font-size:5.6cqw; } .sigp-resena-quote footer{ font-size:3cqw; }
  /* Turnos: tipografía mayor en vertical */
  .sigp-turnos-tit{ font-size:8cqw; } .sigp-turnos-sub{ font-size:3cqw; }
  .sigp-turnos-lista .quien b{ font-size:4.2cqw; } .sigp-turnos-lista .quien em{ font-size:2.6cqw; }
  .sigp-turnos-lista .prox{ font-size:6cqw; } .sigp-turnos-lista .cupos{ font-size:2.6cqw; }
  /* Social: apila imagen sobre texto en vertical, tipografía mayor */
  .sigp-social-card{ grid-template-columns:1fr; width:min(88cqw,80cqh); max-height:84cqh; }
  .sigp-social-card .img{ min-height:34cqh; }
  .sigp-social-card .ava{ width:9cqw; height:9cqw; } .sigp-social-card .ava.ini{ font-size:4cqw; }
  .sigp-social-card .who b{ font-size:3.6cqw; } .sigp-social-card .who span{ font-size:2.6cqw; }
  .sigp-social-card .net{ font-size:2.2cqw; padding:.6cqh 2.4cqw; }
  .sigp-social-card .txt{ font-size:3.6cqw; -webkit-line-clamp:5; }
  .sigp-social-card.solo-texto .txt{ font-size:4.6cqw; }
  .sigp-social-card .foot{ font-size:2.8cqw; } .sigp-social-card .foot .cta{ font-size:2.2cqw; }
  /* Combo: apila items sobre precio en vertical */
  .sigp-combo-body{ grid-template-columns:1fr; gap:3cqh; }
  .sigp-combo-name{ font-size:9cqw; } .sigp-combo-items .n{ font-size:3.4cqw; } .sigp-combo-items .pr{ font-size:3cqw; }
  .sigp-combo-items img, .sigp-combo-items .ph{ width:9cqw; height:9cqw; }
  .sigp-combo-price .pack b{ font-size:14cqw; } .sigp-combo-price .antes, .sigp-combo-price .ahorro{ font-size:3cqw; }
  /* HERO: foto arriba, texto abajo (el flip no aplica en vertical) */
  .sigp-hero, .sigp-hero.flip{ grid-template-columns:1fr; grid-template-rows:54% 46%; }
  .sigp-hero.flip .sigp-photo{ order:0; }
  /* MARCO ROTO: marco arriba, texto abajo (pisa la regla base .sigp-hero.sigm) */
  .sigp-hero.sigm, .sigp-hero.sigm.flip{ grid-template-columns:1fr; grid-template-rows:56% 44%; }
  .sigm-fondo, .sigm-frame, .sigm-flat{ inset:10% 9% 13% 9%; }
  .sigm-frame{ border-width:2cqw; }
  .sigm-shadow{ bottom:15%; width:52%; }
  .sigm-pop{ inset:2% 2% 6% 2%; }
  .sigm-texto{ font-size:3.2cqw; max-width:84cqw; }
  .sigp-txt{ justify-content:flex-start; padding:3.4cqh 6cqw 0; }
  .sigp-txt::before{ display:none; }
  .sigp-eyebrow{ font-size:2cqw; margin-bottom:1.2cqh; }
  .sigp-name{ font-size:8.6cqw; }
  .sigp-marca{ font-size:1.9cqw; margin-top:1.4cqh; }
  .sigp-priceline{ margin-top:2.2cqh; gap:2cqw; }
  .sigp-cur{ font-size:5cqw; margin-bottom:1.4cqh; }
  .sigp-amt{ font-size:16cqw; } .sigp-cents{ font-size:5cqw; }
  .sigp-antes{ font-size:3cqw; } .sigp-dcto{ font-size:2.4cqw; padding:.4cqh 1.6cqw; }
  .sigp-etq{ font-size:2.2cqw; padding:.7cqh 2cqw; top:2.4cqh; left:3cqw; }
  .sigp-stock{ font-size:2cqw; margin-top:2.4cqh; gap:2cqw; } .sigp-stock .d{ width:1.6cqw; height:1.6cqw; }
  .sigp-fallback{ width:30cqh; height:30cqh; }
  /* mini-QR de producto: mayor en vertical */
  .sigp-heroqr{ max-width:26cqw; padding:1.2cqh 2cqw 1cqh; bottom:2.4cqh; right:3cqw; }
  .sigp-heroqr svg{ width:11cqh; height:11cqh; } .sigp-heroqr span{ font-size:1.7cqw; }
  /* CARTA: 1 columna */
  .sigp-carta{ padding:7cqh 6cqw 8cqh; }
  .sigp-carta h3{ font-size:5cqw; } .sigp-carta h3 em{ font-size:2.6cqw; }
  .sigp-cols{ column-count:1; }
  .sigp-cat{ margin-bottom:2.8cqh; } .sigp-cat .lbl{ font-size:2.1cqw; }
  .sigp-row{ font-size:2.6cqw; margin:1.1cqh 0; } .sigp-cetq{ font-size:1.7cqw; }
  /* MENSAJE / ANUNCIO */
  .sigp-msg h2{ font-size:8cqw; } .sigp-msg p{ font-size:3.4cqw; max-width:84cqw; }
  .sigp-admsg .who{ font-size:2.4cqw; } .sigp-admsg h2{ font-size:8cqw; } .sigp-admsg p{ font-size:3.4cqw; max-width:84cqw; }
  .sigp-adtag{ font-size:1.8cqw; }
  /* QR grande: apilar texto arriba / QR abajo */
  .sigp-qr{ flex-direction:column; gap:4cqh; padding:9cqh 8cqw; text-align:center; }
  .sigp-qrtxt{ max-width:86cqw; flex:none; }
  .sigp-qrtxt .sigp-eyebrow{ font-size:2.4cqw; }
  .sigp-qrtxt h2{ font-size:7cqw; } .sigp-qrtxt p{ font-size:3cqw; max-width:none; margin:0 auto; }
  .sigp-qrbox svg{ width:44cqw; height:44cqw; } .sigp-qrempty{ width:44cqw; height:44cqw; }
  /* Ticker un poco más alto */
  .sigp-ticker{ font-size:2.2cqw; height:5cqh; }
  .sigp-vcap{ font-size:4.2cqw; max-width:84cqw; }
}
/* ===== TRANSICIONES entre escenas (doble buffer: capa entrante + saliente) ===== */
.sigp-scenes{ position:absolute; inset:0; }
.sigp-layer{ position:absolute; inset:0; }
.sigp-exit *{ animation:none!important; }   /* la escena saliente no re-anima su interior */
.enter-fundido{ animation:sigp-tin .6s ease both; } .exit-fundido{ animation:sigp-tout .6s ease both; }
.enter-deslizar{ animation:sigp-slin .6s cubic-bezier(.7,0,.2,1) both; } .exit-deslizar{ animation:sigp-slout .6s cubic-bezier(.7,0,.2,1) both; }
.enter-zoom{ animation:sigp-zin .6s cubic-bezier(.7,0,.2,1) both; } .exit-zoom{ animation:sigp-zout .6s ease both; }
.enter-corte,.exit-corte{ animation:none; }
@keyframes sigp-tin{ from{opacity:0} to{opacity:1} } @keyframes sigp-tout{ from{opacity:1} to{opacity:0} }
@keyframes sigp-slin{ from{opacity:.25; transform:translateX(6%)} to{opacity:1; transform:none} }
@keyframes sigp-slout{ from{opacity:1; transform:none} to{opacity:0; transform:translateX(-6%)} }
@keyframes sigp-zin{ from{opacity:0; transform:scale(.965)} to{opacity:1; transform:none} }
@keyframes sigp-zout{ from{opacity:1} to{opacity:0; transform:scale(1.03)} }
@media (prefers-reduced-motion:reduce){ .sigp-progress,.sigp-layer,.sigp-fill,.sigp-name,.sigp-hero,.sigp-carta,.sigp-msg,.sigp-ad,.sigp-qr,.sigp-feed,.sigp-cross,.sigp-ph-fg,.sigp-run,.sigp-dotlive,.sigp-stock.low .d,.sigm-pop,.sigm-pop img,.sigm-shadow,.sigm-flat img{ animation:none!important; } .sigp-progress{ width:100%; } .sigp-name{ clip-path:none; } .sigp-ph-fg{ opacity:1; transition:none; } .sigm-pop{ clip-path:none; } }`;

function useReloj() {
  const [t, setT] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 30000); return () => clearInterval(id); }, []);
  return t.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
}

export function SignagePlayer({ data, onScene, campana, anuncios, phygital, chrome = true, patrocinado = false, token, pantallaId }: { data: PlayerData; onScene?: (s: SceneInfo) => void; campana?: CampanaInfo | null; anuncios?: Anuncio[]; phygital?: Phygital | null; chrome?: boolean; patrocinado?: boolean; token?: string; pantallaId?: string }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(0);
  const reloj = useReloj();

  const items = useMemo<Item[]>(
    () => (data.categorias ?? []).flatMap((c) => (c.productos ?? []).map((p) => ({ ...p, categoria: c.categoria }))),
    [data.categorias],
  );
  // Promociones: mismos productos del catálogo, filtrados a los que tienen descuento (cero backend).
  const itemsPromo = useMemo<Item[]>(() => items.filter((it) => (it.descuento ?? 0) > 0 || it.precio_antes), [items]);
  // Más vendidos: bloque server-side (ranking por ventas), rehidratado como catálogo.
  const itemsLentos = useMemo<Item[]>(
    () => (data.lentos ?? []).flatMap((c) => (c.productos ?? []).map((p) => ({ ...p, categoria: c.categoria }))),
    [data.lentos],
  );
  const itemsTop = useMemo<Item[]>(
    () => (data.topVentas ?? []).flatMap((c) => (c.productos ?? []).map((p) => ({ ...p, categoria: c.categoria }))),
    [data.topVentas],
  );
  const itemsVentasHoy = useMemo<Item[]>(
    () => (data.ventasHoy ?? []).flatMap((c) => (c.productos ?? []).map((p) => ({ ...p, categoria: c.categoria }))),
    [data.ventasHoy],
  );
  // Gadgets persistentes: config resuelta del lugar (pantalla ?? grupo); solo con chrome.
  const gadgetsCfg = chrome && data.gadgets && data.gadgets.activo !== false ? data.gadgets : null;

  // Velocidad de la CINTA inferior: px/segundo CONSTANTES (proporcional al largo
  // del contenido — con 300 productos ya no "vuela") × preferencia del lugar
  // (gadgets.ticker: lenta/normal/rapida, hereda pantalla ?? grupo). Antes era
  // una animación fija de 40s sin importar cuántos productos hubiera.
  const tickerRef = useRef<HTMLDivElement>(null);
  const tickerVel = data.gadgets?.ticker ?? 'normal';
  useLayoutEffect(() => {
    const el = tickerRef.current;
    if (!el) return;
    const pxs = tickerVel === 'lenta' ? 45 : tickerVel === 'rapida' ? 120 : 75;
    // el keyframe recorre el 50% del ancho total (contenido duplicado)
    el.style.animationDuration = `${Math.max(15, Math.round(el.scrollWidth / 2 / pxs))}s`;
  }, [items, data.avisos, tickerVel]);
  // Clima del local (Open-Meteo, cada 30 min) — una sola fuente para: escenas con
  // condición `clima` y los gadgets del header (temp/UV). Fail-open: sin dato, las
  // escenas condicionadas se muestran (nunca pantalla vacía) y el header no pinta clima.
  const usaClima = useMemo(
    () => (data.escenas ?? []).some((e) => e.clima) || !!gadgetsCfg?.clima || !!gadgetsCfg?.uv,
    [data.escenas, gadgetsCfg],
  );
  const [clima, setClima] = useState<{ temp: number; wc: number; uv: number; prob: number } | null>(null);
  useEffect(() => {
    if (!usaClima || data.lat == null || data.lon == null) { setClima(null); return; }
    let vivo = true;
    const leer = async () => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${data.lat}&longitude=${data.lon}`
          + '&current=temperature_2m,weather_code&hourly=precipitation_probability&forecast_hours=3&daily=uv_index_max&timezone=auto');
        const j = await r.json() as {
          current?: { temperature_2m?: number; weather_code?: number };
          hourly?: { precipitation_probability?: number[] };
          daily?: { uv_index_max?: number[] };
        };
        if (!vivo) return;
        setClima({
          temp: j.current?.temperature_2m ?? 22,
          wc: j.current?.weather_code ?? 0,
          uv: j.daily?.uv_index_max?.[0] ?? 0,
          prob: Math.max(...(j.hourly?.precipitation_probability ?? [0])),
        });
      } catch { /* sin red: fail-open */ }
    };
    void leer();
    const t = setInterval(() => { void leer(); }, 30 * 60_000);
    return () => { vivo = false; clearInterval(t); };
  }, [usaClima, data.lat, data.lon]);
  const condClima = useMemo(() => {
    if (!clima) return null;
    const lloviendo = (clima.wc >= 51 && clima.wc <= 67) || (clima.wc >= 80 && clima.wc <= 99);
    return lloviendo || clima.prob > 60 ? 'lluvia' : clima.uv >= 7 ? 'sol' : clima.temp <= 16 ? 'frio' : clima.temp >= 29 ? 'calor' : '';
  }, [clima]);
  const escenas = useMemo(
    () => (data.escenas?.length ? data.escenas : DEFAULT_ESCENAS),
    [data.escenas],
  );

  const sequence = useMemo<Slide[]>(() => {
    const construir = (ignorarClima: boolean): Slide[] => {
    const seq: Slide[] = [];
    for (const esc of escenas) {
      const tr = esc.trans;
      // condición de clima por escena: solo se omite si la condición YA se conoce y no coincide
      if (!ignorarClima && esc.clima && condClima != null && condClima !== '' && esc.clima !== condClima) continue;
      if (!ignorarClima && esc.clima && condClima === '') continue;   // clima neutro: las escenas condicionadas descansan
      if (esc.tipo === 'catalogo') items.forEach((it, i) => seq.push({ kind: 'hero', item: it, n: i, dur: esc.dur || 9, trans: tr }));
      else if (esc.tipo === 'promociones') itemsPromo.forEach((it, i) => seq.push({ kind: 'hero', item: it, n: i, dur: esc.dur || 9, trans: tr }));
      else if (esc.tipo === 'top_ventas') itemsTop.forEach((it, i) => seq.push({ kind: 'hero', item: it, n: i, dur: esc.dur || 9, trans: tr }));
      else if (esc.tipo === 'lentos') itemsLentos.forEach((it, i) => seq.push({ kind: 'hero', item: it, n: i, dur: esc.dur || 9, trans: tr }));
      else if (esc.tipo === 'carta') seq.push({ kind: 'carta', dur: esc.dur || 16, titulo: esc.titulo, trans: tr });
      else if (esc.tipo === 'qr') { if (phygital?.activo) seq.push({ kind: 'qr', dur: esc.dur || 12, titulo: esc.titulo, texto: esc.texto, trans: tr }); }
      else if (esc.tipo === 'video') { if (esc.url) seq.push({ kind: 'video', dur: esc.dur || 20, url: esc.url, titulo: esc.titulo, trans: tr }); }
      else if (esc.tipo === 'imagen') { if (esc.url) seq.push({ kind: 'imagen', dur: esc.dur || 10, url: esc.url, titulo: esc.titulo, foco_x: esc.foco_x, foco_y: esc.foco_y, trans: tr }); }
      else if (esc.tipo === 'feed') {
        // cuenta regresiva atada a un combo: la fecha de fin viene del combo vigente
        let fecha = esc.fecha;
        let titulo = esc.titulo;
        if (esc.feed === 'cuenta' && esc.combo_id) {
          const cb = (data.combos ?? []).find((c) => c.id === esc.combo_id);
          if (!cb?.hasta) continue;               // combo vencido/sin fecha → la escena descansa
          fecha = `${cb.hasta}T23:59:59`;
          titulo = titulo || `${cb.nombre} — termina en`;
        }
        seq.push({ kind: 'feed', dur: esc.dur || 10, feed: esc.feed || 'reloj', titulo, lat: esc.lat, lon: esc.lon, fecha, fuente_id: esc.fuente_id, trans: tr });
      }
      else if (esc.tipo === 'layout') { if (esc.zonas?.length) seq.push({ kind: 'layout', dur: esc.dur || 60, zonas: esc.zonas, trans: tr }); }
      else if (esc.tipo === 'cross_sell') seq.push({ kind: 'cross', dur: esc.dur || 12, titulo: esc.titulo, trans: tr });
      else if (esc.tipo === 'combo') (data.combos ?? []).forEach((cb) => seq.push({ kind: 'combo', combo: cb, dur: esc.dur || 12, trans: tr }));
      // la vigencia por HORA se corta client-side: la firma solo rota cada hora
      else if (esc.tipo === 'cupon') (data.cupones ?? []).filter((cp) => !cp.hasta || new Date(cp.hasta).getTime() > Date.now())
        .forEach((cp) => seq.push({ kind: 'cupon', cupon: cp, dur: esc.dur || 12, trans: tr }));
      else if (esc.tipo === 'por_vencer') { if ((data.porVencer ?? []).length) seq.push({ kind: 'porvencer', dur: esc.dur || 14, titulo: esc.titulo, items: data.porVencer ?? [], trans: tr }); }
      else if (esc.tipo === 'salud') { if ((data.salud?.tips ?? []).length) seq.push({ kind: 'salud', dur: esc.dur || 14, titulo: esc.titulo, trans: tr }); }
      else if (esc.tipo === 'social') {
        (data.social ?? [])
          .filter((c) => !esc.red || c.red === esc.red)
          .forEach((c) => c.posts.forEach((p) => seq.push({ kind: 'social', dur: esc.dur || 10, post: p, cuenta: c, trans: tr })));
      }
      else if (esc.tipo === 'ventas_hoy') itemsVentasHoy.forEach((it, i) => seq.push({ kind: 'hero', item: it, n: i, dur: esc.dur || 9, trans: tr }));
      else if (esc.tipo === 'marco') {
        // Marco roto (pop-out "3D"): productos elegidos (precio/stock EN VIVO del
        // catálogo del local) o, sin productos, una imagen recortada de la biblioteca.
        const elegidos = (esc.productos ?? []).map((id) => items.find((it) => it.id === id)).filter(Boolean) as Item[];
        if (elegidos.length) elegidos.forEach((it, i) => seq.push({ kind: 'marco', item: it, n: i, dur: esc.dur || 10, color: esc.marco, trans: tr }));
        else if (esc.url) seq.push({ kind: 'marco', n: 0, url: esc.url, dur: esc.dur || 10, titulo: esc.titulo, texto: esc.texto, color: esc.marco, trans: tr });
      }
      else if (esc.tipo === 'turnos') { if ((data.turnos ?? []).length) seq.push({ kind: 'turnos', dur: esc.dur || 12, titulo: esc.titulo, items: data.turnos ?? [], trans: tr }); }
      else if (esc.tipo === 'resenas') {
        (data.resenas?.items ?? []).forEach((it) => seq.push({
          kind: 'resena', dur: esc.dur || 10, item: it,
          promedio: data.resenas?.promedio ?? 0, total: data.resenas?.total ?? 0, trans: tr,
        }));
      }
      else seq.push({ kind: 'msg', dur: esc.dur || 8, titulo: esc.titulo, texto: esc.texto, trans: tr });
    }
    return seq;
    };
    // Fail-open real: si el filtro de clima dejó la secuencia VACÍA (todas las
    // escenas condicionadas y clima neutro), se reconstruye ignorando el clima —
    // jamás una pantalla en negro (hallazgo de auditoría).
    let seq = construir(false);
    if (!seq.length && escenas.some((e) => e.clima)) seq = construir(true);
    // Retail media: intercala anuncios por share-of-voice (1 en N escenas), round-robin.
    const ads = anuncios ?? [];
    if (!ads.length || seq.length === 0) return seq;
    const cada = Math.max(2, Math.min(...ads.map((a) => a.sov ?? 6)));
    const out: Slide[] = [];
    let ai = 0;
    seq.forEach((s, i) => {
      out.push(s);
      if ((i + 1) % cada === 0) { const ad = ads[ai % ads.length]; ai += 1; out.push({ kind: 'anuncio', dur: ad.dur ?? 8, ad }); }
    });
    return out;
  }, [items, itemsPromo, itemsTop, itemsLentos, itemsVentasHoy, escenas, anuncios, phygital, data.combos, data.cupones, data.porVencer, data.salud, data.social, data.turnos, data.resenas, condClima]);

  const slide = sequence[pos];
  const dur = slide?.dur ?? 9;

  // Avance a la siguiente escena, idempotente por posición de origen (el timer y el
  // onEnded del video pueden llamarlo; solo el primero por posición surte efecto).
  const ultimoAvance = useRef(-1);
  // Al cambiar la secuencia hay que RESETEAR también el guard de avance: si quedó
  // apuntando a la posición 0, el próximo goNext en pos 0 se bloquearía y no habría
  // nuevo timer → pantalla congelada (hallazgo de auditoría).
  useEffect(() => { ultimoAvance.current = -1; setPos(0); }, [sequence.length]);
  const goNext = useCallback(() => {
    setPos((p) => { if (ultimoAvance.current === p) return p; ultimoAvance.current = p; return sequence.length ? (p + 1) % sequence.length : 0; });
  }, [sequence.length]);

  // Sincronía de reloj de pared (423): con plantilla.sync la posición se deriva
  // del RELOJ (epoch % duración del ciclo) — todas las pantallas con la misma
  // plantilla muestran la misma escena al mismo segundo (± el reloj del box,
  // NTP) y se autocorrigen en cada frontera. El contenido ya es idéntico entre
  // pantallas del mismo local (payload memoizado). Videos: loop hasta el corte.
  const sincronizado = !!data.sync && sequence.length > 1;
  useEffect(() => {
    if (!sincronizado) return;
    let t: ReturnType<typeof setTimeout>;
    const tick = () => {
      const D = sequence.reduce((a, s) => a + (s.dur || 9), 0);
      const en = (Date.now() / 1000) % D;
      let acc = 0; let idx = 0;
      for (let i = 0; i < sequence.length; i++) { const d = sequence[i].dur || 9; if (en < acc + d) { idx = i; break; } acc += d; }
      ultimoAvance.current = -1;   // el guard de goNext no aplica en modo reloj
      setPos(idx);
      const resta = acc + (sequence[idx].dur || 9) - en;
      t = setTimeout(tick, Math.max(250, resta * 1000));
    };
    tick();
    return () => clearTimeout(t);
  }, [sincronizado, sequence]);

  useEffect(() => {
    if (sincronizado || sequence.length <= 1) return;
    const ms = (sequence[pos]?.dur ?? 9) * 1000;   // para video-archivo actúa de tope; onEnded avanza antes
    const t = setTimeout(goNext, ms);
    return () => clearTimeout(t);
  }, [pos, sequence, goNext, sincronizado]);

  // Transición entre escenas: mantiene la escena saliente durante ~650ms para cruzar
  // con la entrante (crossfade / deslizar / zoom). La escena entrante define el estilo.
  const curTrans: TransKind = slide?.kind === 'anuncio' ? 'corte' : (slide?.trans ?? 'fundido');
  const prevRef = useRef<{ slide: Slide; pos: number } | null>(null);
  const [exiting, setExiting] = useState<{ slide: Slide; pos: number; trans: TransKind } | null>(null);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = slide ? { slide, pos } : null;
    if (!slide || !prev || prev.pos === pos || reduceMotion || curTrans === 'corte') { setExiting(null); return; }
    setExiting({ slide: prev.slide, pos: prev.pos, trans: curTrans });
    const id = setTimeout(() => setExiting(null), 650);
    return () => clearTimeout(id);
  }, [pos]); // eslint-disable-line react-hooks/exhaustive-deps

  // notifica escena (proof-of-play) + count-up del precio
  useEffect(() => {
    const s = sequence[pos];
    if (s && onScene) {
      if (s.kind === 'hero') onScene({ tipo: 'catalogo', ref: s.item.id, titulo: s.item.nombre, dur: s.dur });
      else if (s.kind === 'carta') onScene({ tipo: 'carta', ref: null, titulo: s.titulo ?? 'Carta', dur: s.dur });
      else if (s.kind === 'anuncio') onScene({ tipo: 'anuncio', ref: s.ad.id, titulo: s.ad.titulo ?? s.ad.anunciante, dur: s.dur });
      else if (s.kind === 'qr') onScene({ tipo: 'qr', ref: null, titulo: s.titulo ?? 'QR', dur: s.dur });
      else if (s.kind === 'video') onScene({ tipo: 'video', ref: null, titulo: s.titulo ?? 'Video', dur: s.dur });
      else if (s.kind === 'imagen') onScene({ tipo: 'imagen', ref: null, titulo: s.titulo ?? 'Imagen', dur: s.dur });
      else if (s.kind === 'feed') onScene({ tipo: 'feed', ref: s.feed, titulo: s.titulo ?? s.feed, dur: s.dur });
      else if (s.kind === 'layout') onScene({ tipo: 'layout', ref: null, titulo: 'Diseño', dur: s.dur });
      else if (s.kind === 'cross') onScene({ tipo: 'cross_sell', ref: null, titulo: s.titulo ?? 'Cross-sell', dur: s.dur });
      else if (s.kind === 'combo') onScene({ tipo: 'combo', ref: s.combo.id, titulo: s.combo.nombre, dur: s.dur });
      else if (s.kind === 'cupon') onScene({ tipo: 'cupon', ref: s.cupon.codigo, titulo: s.cupon.nombre, dur: s.dur });
      else if (s.kind === 'porvencer') onScene({ tipo: 'por_vencer', ref: null, titulo: s.titulo ?? 'Por vencer', dur: s.dur });
      else if (s.kind === 'salud') onScene({ tipo: 'salud', ref: null, titulo: s.titulo ?? 'Salud del día', dur: s.dur });
      else if (s.kind === 'social') onScene({ tipo: 'social', ref: s.cuenta.id, titulo: `@${s.cuenta.handle.replace(/^@/, '')}`, dur: s.dur });
      else if (s.kind === 'turnos') onScene({ tipo: 'turnos', ref: null, titulo: s.titulo ?? 'Turnos de hoy', dur: s.dur });
      else if (s.kind === 'resena') onScene({ tipo: 'resenas', ref: null, titulo: 'Reseñas', dur: s.dur });
      else if (s.kind === 'marco') onScene({ tipo: 'marco', ref: s.item?.id ?? null, titulo: s.item?.nombre ?? s.titulo ?? 'Marco roto', dur: s.dur });
      else onScene({ tipo: 'mensaje', ref: null, titulo: s.titulo ?? null, dur: s.dur });
    }
    // count-up SOLO en hero/marco de este nivel (evita capturar el precio de un player anidado en un layout)
    const kNow = sequence[pos]?.kind;
    const el = kNow === 'hero' || kNow === 'marco' ? (stageRef.current?.querySelector('.sigp-enter .sigp-amt') as HTMLElement | null) : null;
    if (!el) return;
    const target = Number(el.dataset.val || '0');
    if (reduceMotion || el.classList.contains('struck')) { el.textContent = String(target); return; }
    let raf = 0; const t0 = performance.now();
    const step = (t: number) => { const k = Math.min(1, (t - t0) / 600); el.textContent = String(Math.round(target * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [pos]); // eslint-disable-line react-hooks/exhaustive-deps

  const tk = data.tema?.tokens ?? {};
  const styleVars = {
    ['--dur' as string]: `${dur}s`,
    ...(tk.paper ? { ['--paper' as string]: tk.paper } : {}), ...(tk.paper2 ? { ['--paper2' as string]: tk.paper2 } : {}),
    ...(tk.ink ? { ['--ink' as string]: tk.ink } : {}), ...(tk.soft ? { ['--soft' as string]: tk.soft } : {}),
    ...(tk.accent ? { ['--accent' as string]: tk.accent } : {}), ...(tk.sage ? { ['--sage' as string]: tk.sage } : {}),
    ...(tk.oxido ? { ['--oxido' as string]: tk.oxido } : {}), ...(tk.topo ? { ['--topo' as string]: tk.topo } : {}),
  } as React.CSSProperties;

  const label = slide?.kind === 'carta' ? 'CARTA' : slide?.kind === 'msg' ? 'INFO' : slide?.kind === 'anuncio' ? 'PUBLICIDAD' : slide?.kind === 'qr' ? 'QR' : slide?.kind === 'video' ? 'VIDEO' : slide?.kind === 'imagen' ? 'IMAGEN' : slide?.kind === 'feed' ? 'INFO' : slide?.kind === 'layout' ? 'MIX' : slide?.kind === 'cross' ? 'PARA TI' : slide?.kind === 'combo' ? 'COMBO' : slide?.kind === 'cupon' ? 'CUPÓN' : slide?.kind === 'porvencer' ? 'LIQUIDACIÓN' : slide?.kind === 'salud' ? 'SALUD' : slide?.kind === 'social' ? 'SÍGUENOS' : slide?.kind === 'turnos' ? 'AGENDA HOY' : slide?.kind === 'resena' ? 'OPINIONES' : slide?.kind === 'marco' ? 'DESTACADO' : `${(slide?.kind === 'hero' ? slide.n : 0) + 1} / ${items.length}`;

  // QR phygital: construye URLs absolutas hacia el storefront (o URL propia).
  const ph = phygital?.activo ? phygital : null;
  const phBase = (ph?.url_base || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/, '');
  // sp = id de la PANTALLA (no el token): la tienda lo devuelve como beacon de
  // escaneo (422) — cada QR escaneado cuenta para la pantalla que lo exhibió.
  const phQs = [ph?.utm ? `utm_source=${encodeURIComponent(ph.utm)}&utm_medium=signage` : '', pantallaId ? `sp=${pantallaId}` : '']
    .filter(Boolean).join('&');
  const phUtm = phQs ? `?${phQs}` : '';
  const tiendaUrl = ph?.tienda_slug ? `${phBase}/tienda/${ph.tienda_slug}${phUtm}` : '';
  const productoUrl = (webSlug?: string | null) => (ph?.tienda_slug && webSlug ? `${phBase}/tienda/${ph.tienda_slug}/p/${webSlug}${phUtm}` : '');
  const escenaQrUrl = ph ? (ph.modo === 'url' ? (ph.url_personalizada || '') : tiendaUrl) : '';

  // Pinta UNA escena (reutilizable para la capa entrante y la saliente durante la transición).
  const renderScene = (s: Slide | undefined) => {
    if (!s) return null;
    if (s.kind === 'anuncio') return <AdScene ad={s.ad} />;
    if (s.kind === 'qr') return (
      <div className="sigp-qr">
        <div className="sigp-qrtxt">
          <div className="sigp-eyebrow">{ph?.modo === 'url' ? 'Escanéalo' : 'Compra en línea'}</div>
          <h2 className="sigp-name">{s.titulo || (ph?.modo === 'url' ? 'Escanea con tu celular' : 'Nuestra tienda en tu bolsillo')}</h2>
          <p>{s.texto || ph?.cta || 'Escanéalo con la cámara de tu celular'}</p>
        </div>
        <div className="sigp-qrbox">
          {escenaQrUrl ? <QRCodeSVG value={escenaQrUrl} size={512} level="M" bgColor="#ffffff" fgColor="#101010" /> : <div className="sigp-qrempty"><Package style={{ width: '4cqw', height: '4cqw' }} /></div>}
        </div>
      </div>
    );
    if (s.kind === 'carta') return (
      <div className="sigp-carta">
        <h3>{s.titulo || 'La carta'} <em>· {data.local}</em></h3>
        <div className="sigp-cols">
          {(data.categorias ?? []).map((c) => (
            <div key={c.categoria} className="sigp-cat">
              <div className="lbl">{c.categoria}</div>
              {c.productos.map((p) => (
                <div key={p.id} className={`sigp-row ${p.agotado ? 'out' : ''}`}>
                  <span className="n">{p.nombre}{p.etiqueta && <span className="sigp-cetq" style={{ background: p.etiqueta_color || 'var(--accent)' }}>{p.etiqueta}</span>}</span><span className="lead" />
                  <span className="p">{p.agotado ? '—' : `$${Number(p.precio).toFixed(2)}`}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
    if (s.kind === 'msg') return <div className="sigp-msg"><h2>{s.titulo || data.empresa}</h2>{s.texto && <p>{s.texto}</p>}</div>;
    if (s.kind === 'video') return <VideoScene url={s.url} titulo={s.titulo} loop={sequence.length <= 1 || sincronizado} onDone={goNext} />;
    if (s.kind === 'imagen') return (
      <div className="sigp-imgscene">
        <img src={s.url} alt={s.titulo || ''} style={{ objectPosition: `${s.foco_x ?? 50}% ${s.foco_y ?? 50}%` }} />
        {s.titulo && <div className="sigp-vcap">{s.titulo}</div>}
      </div>
    );
    if (s.kind === 'feed') return <FeedScene feed={s.feed} titulo={s.titulo} lat={s.lat} lon={s.lon} fecha={s.fecha} fuenteId={s.fuente_id} feeds={data.feeds} />;
    if (s.kind === 'cross') return <CrossSellScene token={token} titulo={s.titulo} fallback={itemsTop.length ? itemsTop : items} />;
    if (s.kind === 'combo') return <ComboScene combo={s.combo} />;
    if (s.kind === 'cupon') return <CuponScene cupon={s.cupon} />;
    if (s.kind === 'social') return <SocialScene post={s.post} cuenta={s.cuenta} />;
    if (s.kind === 'turnos') return <TurnosScene titulo={s.titulo} items={s.items} />;
    if (s.kind === 'resena') return <ResenaScene item={s.item} promedio={s.promedio} total={s.total} />;
    if (s.kind === 'porvencer') return <PorVencerScene titulo={s.titulo} items={s.items} />;
    if (s.kind === 'salud') return <SaludScene titulo={s.titulo} salud={data.salud ?? null} />;
    if (s.kind === 'layout') return (
      <div className="sigp-layout">
        {s.zonas.map((z, zi) => (
          <div key={zi} className="sigp-zona" style={{ left: `${z.rect[0]}%`, top: `${z.rect[1]}%`, width: `${z.rect[2]}%`, height: `${z.rect[3]}%` }}>
            <SignagePlayer chrome={false} data={{ ...data, escenas: z.escenas }} campana={null} anuncios={[]} phygital={phygital} token={token} pantallaId={pantallaId} />
          </div>
        ))}
      </div>
    );
    if (s.kind === 'marco') return <MarcoScene s={s} />;
    // hero
    const it = s.item; const fl = s.n % 2 === 1;
    const hq = ph?.mostrar_en_producto ? productoUrl(it.web_slug) : '';
    return (
      <div className={`sigp-hero ${fl ? 'flip' : ''}`}>
        <div className={`sigp-photo ${it.agotado ? 'out' : ''}`}>
          {it.etiqueta && <span className="sigp-etq" style={{ background: it.etiqueta_color || 'var(--accent)' }}>{it.etiqueta}</span>}
          {hq && !it.agotado && (
            <div className="sigp-heroqr">
              <QRCodeSVG value={hq} size={256} level="M" bgColor="#ffffff" fgColor="#101010" />
              <span>{ph?.cta || 'Escanéalo para comprarlo'}</span>
            </div>
          )}
          <PhotoFrame src={it.imagen} alt={it.nombre} nombre={it.nombre} />
        </div>
        <HeroTexto it={it} />
      </div>
    );
  };

  return (
    <div ref={stageRef} className="sigp-stage" data-preset={data.tema?.preset ?? 'calido'} style={styleVars}
         onClick={() => sequence.length > 1 && setPos((p) => (p + 1) % sequence.length)}>
      {chrome && sequence.length > 0 && <div key={`p${pos}`} className="sigp-progress" />}
      {chrome && patrocinado && <span className="sigp-patro">Publicidad</span>}

      {chrome && (
        <div className="sigp-top">
          <div className="sigp-brand">
            {data.tema?.logo ? <img className="sigp-logo" src={data.tema.logo} alt={data.empresa ?? ''} /> : <span>{data.empresa}</span>}
            <small>{data.local}</small>
          </div>
          <div className="sigp-live">
            {gadgetsCfg?.clima && clima && (
              <><span className="sigg-wx"><em>{wmo(clima.wc).icon}</em> {Math.round(clima.temp)}°</span><span className="sep">·</span></>
            )}
            {gadgetsCfg?.uv && clima && clima.uv >= 6 && new Date().getHours() >= 10 && new Date().getHours() < 16 && (
              <><span className="sigg-uv">UV {Math.round(clima.uv)}</span><span className="sep">·</span></>
            )}
            {sequence.length > 0 && <><span className="idx">{label}</span><span className="sep">·</span></>}
            {gadgetsCfg?.fecha && <><span>{new Date().toLocaleDateString(LOCALE, { weekday: 'short', day: 'numeric' })}</span><span className="sep">·</span></>}
            <span>{reloj}</span><span className="sigp-dotlive" /><span>EN VIVO</span>
          </div>
        </div>
      )}

      {items.length === 0 && sequence.length === 0 ? (
        <div className="sigp-empty"><Package style={{ width: '5cqw', height: '5cqw' }} /><span style={{ fontSize: '1.4cqw' }}>Sin contenido para mostrar.</span></div>
      ) : (
        <div className="sigp-scenes">
          {/* La capa SALIENTE no re-monta videos/HLS: recargaría el stream solo para
              el fade y su onError podría adelantar la escena en curso (auditoría). */}
          {exiting && <div key={`x${exiting.pos}`} className={`sigp-layer sigp-exit exit-${exiting.trans}`}>{exiting.slide?.kind === 'video' ? null : renderScene(exiting.slide)}</div>}
          <div key={`e${pos}`} className={`sigp-layer sigp-enter enter-${curTrans}`}>{renderScene(slide)}</div>
        </div>
      )}

      {campana?.efecto ? <CampaignOverlay campana={campana} /> : null}
      {gadgetsCfg && (gadgetsCfg.items?.length ?? 0) > 0
        && !(gadgetsCfg.ocultar_en ?? ['video', 'anuncio']).includes(slide?.kind ?? '')
        && !(patrocinado && gadgetsCfg.ocultar_patrocinado)
        && <GadgetsOverlay cfg={gadgetsCfg} local={data.local} campana={campana} combos={data.combos} />}

      {chrome && (items.length > 0 || (data.avisos ?? []).length > 0) && (
        <div className="sigp-ticker"><div ref={tickerRef} className="sigp-run">{[0, 1].map((r) => (
          <span key={r} style={{ display: 'inline-flex' }}>
            {(data.avisos ?? []).map((a, ai) => (
              <span key={`av${ai}-${r}`} style={{ display: 'inline-flex' }}>
                <span className="sigp-avi">📢 {a}</span>
                <span className="sigp-tsep">/</span>
              </span>
            ))}
            {items.map((p) => (
              <span key={p.id + '-' + r} style={{ display: 'inline-flex' }}>
                <span className="sigp-it"><b>{p.nombre}</b> {p.agotado ? <s>—</s> : `$${Number(p.precio).toFixed(2)}`}</span>
                <span className="sigp-tsep">/</span>
              </span>
            ))}
          </span>
        ))}</div></div>
      )}
    </div>
  );
}

// Contrato de foto: fondo desenfocado (cover) + primer plano nítido que NUNCA recorta
// mal (contain para fotos muy verticales/panorámicas, cover para las cercanas al marco).
// Fade-in al cargar; si la imagen falla o no existe, degrada a un gradiente con ícono.
function PhotoFrame({ src, alt, nombre }: { src: string | null; alt: string; nombre: string }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [fit, setFit] = useState<'cover' | 'contain'>('cover');
  useEffect(() => { setFailed(false); setLoaded(false); setFit('cover'); }, [src]);
  if (!src || failed) {
    return (
      <div className="sigp-ph-fallback" style={{ background: `linear-gradient(145deg, hsl(${hue(nombre)} 45% 42%), hsl(${(hue(nombre) + 30) % 360} 50% 26%))` }}>
        <Package className="sigp-fallback" />
      </div>
    );
  }
  const onLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const a = img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
    setFit(a < 0.72 || a > 2.2 ? 'contain' : 'cover');
    setLoaded(true);
  };
  return (
    <>
      <div className="sigp-ph-bg" style={{ backgroundImage: `url("${src}")` }} />
      <div className={`sigp-ph-fg ${fit} ${loaded ? 'on' : ''}`}>
        <img src={src} alt={alt} onLoad={onLoad} onError={() => setFailed(true)} />
      </div>
    </>
  );
}

const estadoStock = (p: Item) => (p.agotado ? 'out' : p.controla_stock && (p.stock ?? 0) > 0 && (p.stock ?? 0) <= 5 ? 'low' : 'ok');

// Columna de texto del hero (categoría + nombre + marca + precio con count-up +
// semáforo de stock). La comparten la escena catálogo/hero y la escena marco.
function HeroTexto({ it }: { it: Item }) {
  const [d, c] = Number(it.precio).toFixed(2).split('.');
  return (
    <div className="sigp-txt">
      <div className="sigp-eyebrow">{it.categoria}</div>
      <h2 className="sigp-name">{it.nombre}</h2>
      {it.marca && <div className="sigp-marca">{it.marca}</div>}
      <div className="sigp-priceline">
        {(it.precio_antes || (it.descuento ?? 0) > 0) && (
          <span className="sigp-promo">
            {it.precio_antes ? <span className="sigp-antes">${Number(it.precio_antes).toFixed(2)}</span> : null}
            {(it.descuento ?? 0) > 0 ? <span className="sigp-dcto">-{it.descuento}%</span> : null}
          </span>
        )}
        <span className="sigp-cur">$</span>
        <span className={`sigp-amt ${it.agotado ? 'struck' : ''}`} data-val={d}>{d}</span>
        <span className="sigp-cents">{c}</span>
      </div>
      <StockLinea p={it} kind={estadoStock(it)} />
    </div>
  );
}

// ===== Escena MARCO ROTO (pop-out "3D") ======================================
// Recorte del producto en runtime, con cache en memoria: la foto de catálogo
// (data-URI) pasa por recortarFondoDataUri UNA vez; el resultado (o null si el
// fondo no es recortable) se reutiliza en cada pasada del bucle. El cap evita
// crecer sin límite en un box encendido 24/7.
const recortesCache = new Map<string, string | null>();
function useRecorte(src: string | null | undefined): string | null | undefined {
  const [v, setV] = useState<string | null | undefined>(() => (src ? recortesCache.get(src) : null));
  useEffect(() => {
    if (!src) { setV(null); return; }
    if (recortesCache.has(src)) { setV(recortesCache.get(src)); return; }
    let vivo = true;
    setV(undefined);                               // procesando…
    recortarFondoDataUri(src)
      .then((r) => { if (recortesCache.size > 48) recortesCache.clear(); recortesCache.set(src, r); if (vivo) setV(r); })
      .catch(() => { recortesCache.set(src, null); if (vivo) setV(null); });
    return () => { vivo = false; };
  }, [src]);
  return v;
}

// El truco vive en las CAPAS: fondo interior (z1) → marco dibujado (z2) →
// producto SIN recorte de caja (z3) que arranca clipeado a la boca del marco y
// "rompe" el borde al crecer (clip-path → 0), con sombra proyectada sobre el
// plano del marco y levitación. Si la foto no es recortable (fondo ambientado)
// degrada a foto enmarcada clásica — nunca una "estampilla" cuadrada flotando.
function MarcoScene({ s }: { s: Extract<Slide, { kind: 'marco' }> }) {
  const fl = s.n % 2 === 1;
  const fuente = s.item ? s.item.imagen : s.url ?? null;
  const recorte = useRecorte(fuente);
  const conPop = typeof recorte === 'string';
  return (
    <div className={`sigp-hero sigm ${fl ? 'flip' : ''}`}>
      <div className="sigm-side">
        <div className="sigm-fondo" />
        <div className="sigm-frame" style={s.color ? { borderColor: s.color } : undefined} />
        {conPop ? (
          <>
            <div className="sigm-shadow" />
            <div className={`sigm-pop ${s.item?.agotado ? 'out' : ''}`}>
              <img src={recorte} alt={s.item?.nombre ?? s.titulo ?? ''} />
            </div>
          </>
        ) : (
          <div className={`sigm-flat ${s.item?.agotado ? 'out' : ''}`}>
            {fuente
              ? <img src={fuente} alt={s.item?.nombre ?? s.titulo ?? ''} />
              : <Package className="sigm-vacio" />}
          </div>
        )}
        {s.item?.etiqueta && <span className="sigp-etq" style={{ background: s.item.etiqueta_color || 'var(--accent)' }}>{s.item.etiqueta}</span>}
      </div>
      {s.item ? <HeroTexto it={s.item} /> : (
        <div className="sigp-txt">
          <div className="sigp-eyebrow">Destacado</div>
          <h2 className="sigp-name">{s.titulo || 'Nuevo'}</h2>
          {s.texto && <p className="sigm-texto">{s.texto}</p>}
        </div>
      )}
    </div>
  );
}

// Anuncio (retail media) con degradado si el arte del banner falla.
function AdScene({ ad }: { ad: Anuncio }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [ad.id]);
  const esBanner = ad.tipo === 'banner' && ad.media && !failed;
  return (
    <div className={`sigp-ad ${esBanner ? 'banner' : 'msg'}`}>
      {esBanner ? (
        <img className="sigp-adimg" src={ad.media as string} alt={ad.titulo || ad.anunciante} onError={() => setFailed(true)} />
      ) : (
        <div className="sigp-admsg">
          <span className="who">{ad.anunciante}</span>
          <h2>{ad.titulo || ad.anunciante}</h2>
          {ad.texto && <p>{ad.texto}</p>}
        </div>
      )}
      <span className="sigp-adtag">Publicidad</span>
    </div>
  );
}

// Escena de VIDEO. Archivo directo (mp4/webm) vía <video> (avanza al terminar) o
// YouTube/Vimeo vía <iframe> (avanza por el temporizador de la escena). Siempre en
// mute + autoplay (los navegadores bloquean autoplay con audio). El origen es una URL
// (no data-URI) → no infla el JSON offline; sin red, el <video> falla y la escena avanza.
function videoProvider(url: string): 'youtube' | 'vimeo' | 'hls' | 'file' {
  if (/(?:youtube\.com|youtu\.be)/i.test(url)) return 'youtube';
  if (/vimeo\.com/i.test(url)) return 'vimeo';
  if (/\.m3u8(\?|$)/i.test(url)) return 'hls';
  return 'file';
}

// Stream HLS (.m3u8 — TV en vivo / canal corporativo). hls.js SIEMPRE (el soporte
// nativo de WebView/Chromium es inconsistente), con import dinámico para no
// inflar el bundle base, y WATCHDOG: error de media → recover; error fatal o
// stall largo → recrear el player. La duración de la escena es el tope (un
// stream en vivo no "termina"). ⚠ Legal: solo streams propios o con derechos.
function HlsVideo({ url, onFail }: { url: string; onFail: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let destruido = false;
    let hls: { destroy: () => void; recoverMediaError: () => void } | null = null;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const armarStall = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => { if (!destruido) reiniciar(); }, 20_000);   // 20 s sin avanzar → reinicio
    };
    const reiniciar = () => { hls?.destroy(); if (!destruido) void montar(); };
    const montar = async () => {
      try {
        const { default: Hls } = await import('hls.js');
        if (destruido) return;
        if (!Hls.isSupported()) { onFail(); return; }
        const h = new Hls({ maxBufferLength: 20, backBufferLength: 10 });   // buffer acotado (boxes de 2 GB)
        hls = h;
        h.loadSource(url);
        h.attachMedia(video);
        h.on(Hls.Events.MANIFEST_PARSED, () => { void video.play().catch(() => undefined); armarStall(); });
        h.on(Hls.Events.ERROR, (_e: unknown, d: { fatal: boolean; type: string }) => {
          if (!d.fatal) return;
          if (d.type === Hls.ErrorTypes.MEDIA_ERROR) h.recoverMediaError();
          else reiniciar();
        });
      } catch { onFail(); }
    };
    const onTime = () => armarStall();
    video.addEventListener('timeupdate', onTime);
    void montar();
    return () => {
      destruido = true;
      if (stallTimer) clearTimeout(stallTimer);
      video.removeEventListener('timeupdate', onTime);
      hls?.destroy();
    };
  }, [url, onFail]);
  return <video ref={ref} autoPlay muted playsInline />;
}
function videoEmbed(url: string, provider: 'youtube' | 'vimeo', loop: boolean): string {
  if (provider === 'youtube') {
    // PLAYLIST completa (list=PL…): el embed videoseries la reproduce en secuencia
    const list = url.match(/[?&]list=([\w-]+)/)?.[1];
    if (list) {
      return `https://www.youtube.com/embed/videoseries?list=${list}&autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&playsinline=1${loop ? '&loop=1' : ''}`;
    }
    const id = url.match(/(?:youtu\.be\/|v=|\/embed\/)([\w-]{6,})/)?.[1] ?? '';
    const lp = loop ? `&loop=1&playlist=${id}` : '';
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&playsinline=1${lp}`;
  }
  const id = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1] ?? '';
  return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&controls=0&playsinline=1${loop ? '&loop=1' : ''}`;
}

function VideoScene({ url, titulo, loop, onDone }: { url: string; titulo?: string | null; loop: boolean; onDone: () => void }) {
  const provider = videoProvider(url);
  const doneRef = useRef(false);
  const loopRef = useRef(loop); loopRef.current = loop;
  const onDoneRef = useRef(onDone); onDoneRef.current = onDone;
  const finish = () => { if (!doneRef.current && !loopRef.current) { doneRef.current = true; onDoneRef.current(); } };
  // onFail ESTABLE (por refs): si fuera inline, cada re-render del player (reloj
  // cada 30s) desmontaría y recrearía el stream HLS completo (hallazgo de auditoría).
  const onFail = useCallback(() => { setTimeout(() => { if (!doneRef.current && !loopRef.current) { doneRef.current = true; onDoneRef.current(); } }, 800); }, []);
  useEffect(() => { doneRef.current = false; }, [url]);
  return (
    <div className="sigp-video">
      {provider === 'file' ? (
        <video src={url} autoPlay muted playsInline loop={loop} onEnded={finish} onError={() => setTimeout(finish, 800)} />
      ) : provider === 'hls' ? (
        <HlsVideo url={url} onFail={onFail} />
      ) : (
        <iframe src={videoEmbed(url, provider, loop)} title={titulo || 'video'} allow="autoplay; encrypted-media; picture-in-picture" />
      )}
      {titulo && <div className="sigp-vcap">{titulo}</div>}
    </div>
  );
}

// ---- Feeds externos (widgets client-side, sin API key ni worker) -----------
type ClimaVal = { temp: number; code: number; max: number; min: number };
interface OMResp { current: { temperature_2m: number; weather_code: number }; daily: { temperature_2m_max: number[]; temperature_2m_min: number[] } }

function wmo(code: number): { icon: string; label: string } {
  if (code === 0) return { icon: '☀️', label: 'Despejado' };
  if (code <= 3) return { icon: '⛅', label: 'Parcialmente nublado' };
  if (code <= 48) return { icon: '🌫️', label: 'Niebla' };
  if (code <= 57) return { icon: '🌦️', label: 'Llovizna' };
  if (code <= 67) return { icon: '🌧️', label: 'Lluvia' };
  if (code <= 77) return { icon: '🌨️', label: 'Nieve' };
  if (code <= 82) return { icon: '🌧️', label: 'Chubascos' };
  if (code <= 86) return { icon: '🌨️', label: 'Chubascos de nieve' };
  return { icon: '⛈️', label: 'Tormenta' };
}

// Clima por Open-Meteo (gratis, sin API key, CORS abierto). Cachea el último dato
// en IndexedDB para tolerar cortes de red.
function ClimaFeed({ titulo, lat, lon }: { titulo?: string | null; lat?: number; lon?: number }) {
  const [w, setW] = useState<ClimaVal | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancel = false;
    const key = `sig_clima:${lat},${lon}`;
    (async () => {
      if (lat == null || lon == null) { setErr(true); return; }
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&daily=temperature_2m_max,temperature_2m_min&timezone=auto`);
        const j = (await r.json()) as OMResp;
        const val: ClimaVal = { temp: Math.round(j.current.temperature_2m), code: j.current.weather_code, max: Math.round(j.daily.temperature_2m_max[0]), min: Math.round(j.daily.temperature_2m_min[0]) };
        if (!cancel) { setW(val); void cacheSet(key, { val }); }
      } catch {
        const c = await cacheGet<{ val: ClimaVal }>(key);
        if (!cancel) { if (c?.val) setW(c.val); else setErr(true); }
      }
    })();
    return () => { cancel = true; };
  }, [lat, lon]);
  if (err) return <div className="sigp-feed clima">{titulo && <div className="sigp-feed-tit">{titulo}</div>}<div className="sigp-feed-fecha">Sin datos de clima</div></div>;
  if (!w) return <div className="sigp-feed clima"><div className="sigp-feed-fecha">Cargando clima…</div></div>;
  const wm = wmo(w.code);
  return (
    <div className="sigp-feed clima">
      {titulo && <div className="sigp-feed-tit">{titulo}</div>}
      <div className="sigp-clima-main"><span className="ic">{wm.icon}</span><span className="t">{w.temp}°</span></div>
      <div className="sigp-feed-fecha">{wm.label} · máx {w.max}° / mín {w.min}°</div>
    </div>
  );
}

// Cross-sell en tiempo real: sondea sig_contexto(token) y muestra "también te puede
// interesar" según el carrito en curso del local. Sin datos → cae a los más vendidos.
type Reco = { nombre: string; marca: string | null; precio: number; descuento?: number };
function ComboScene({ combo }: { combo: Combo }) {
  return (
    <div className="sigp-combo">
      <div className="sigp-combo-head">
        {combo.etiqueta && <div className="sigp-combo-etq">{combo.etiqueta}</div>}
        <h2 className="sigp-combo-name">{combo.nombre}</h2>
        {combo.descripcion && <p className="sigp-combo-desc">{combo.descripcion}</p>}
      </div>
      <div className="sigp-combo-body">
        <ul className="sigp-combo-items">
          {combo.items.map((it) => (
            <li key={it.id}>
              {it.imagen ? <img src={it.imagen} alt="" /> : <span className="ph"><Package /></span>}
              <span className="n">{it.cantidad > 1 ? `${it.cantidad}× ` : ''}{it.nombre}{it.marca && <em> · {it.marca}</em>}</span>
              <span className="pr">${Number(it.precio).toFixed(2)}</span>
            </li>
          ))}
        </ul>
        <div className="sigp-combo-price">
          {combo.descuento > 0 && <div className="ahorro">AHORRA {Number(combo.descuento)}% · ${Number(combo.ahorro).toFixed(2)}</div>}
          <div className="antes">Por separado ${Number(combo.suma).toFixed(2)}</div>
          <div className="pack"><span>Precio pack</span><b>${Number(combo.precio_pack).toFixed(2)}</b></div>
        </div>
      </div>
    </div>
  );
}

/* Cupón canjeable en caja: ticket con borde troquelado, código GIGANTE y vigencia
   con hora. El descuento que promete lo cobra pos_registrar_venta con el mismo
   código (coherencia pantalla↔caja). */
function CuponScene({ cupon }: { cupon: Cupon }) {
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 30_000); return () => clearInterval(id); }, []);
  const hasta = cupon.hasta ? new Date(cupon.hasta) : null;
  const ms = hasta ? hasta.getTime() - Date.now() : 0;
  const horasRestantes = ms > 0 ? ms / 3600e3 : 0;
  const cuenta = hasta && ms > 0 && horasRestantes < 48
    ? `${Math.floor(ms / 3600e3)}h ${String(Math.floor((ms % 3600e3) / 60e3)).padStart(2, '0')}m`
    : null;
  const valor = cupon.tipo === 'pct' ? `-${Number(cupon.valor)}%` : `-$${Number(cupon.valor).toFixed(2)}`;
  return (
    <div className="sigp-cupon">
      <div className="sigp-cupon-card">
        <div className="sigp-cupon-eyebrow">Cupón · di el código en caja</div>
        <div className="sigp-cupon-valor">{valor}</div>
        <h2 className="sigp-cupon-name">{cupon.nombre}</h2>
        {cupon.producto && <p className="sigp-cupon-desc">En {cupon.producto}</p>}
        {!cupon.producto && cupon.descripcion && <p className="sigp-cupon-desc">{cupon.descripcion}</p>}
        <div className="sigp-cupon-code">{cupon.codigo}</div>
        <div className="sigp-cupon-meta">
          {hasta && <span>Válido hasta {hasta.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })} {hasta.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}</span>}
          {cuenta && <span className="urge">⏳ termina en {cuenta}</span>}
          {cupon.restantes != null && cupon.restantes <= 20 && <span className="urge">¡Solo quedan {cupon.restantes}!</span>}
        </div>
      </div>
    </div>
  );
}

/* Gadgets persistentes: chips discretos sobre el contenido (saludo de campaña,
   cuenta regresiva, texto del local). Clima/UV NO van aquí: viven en el header. */
function CuentaMini({ titulo, hasta }: { titulo?: string | null; hasta: Date }) {
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 30_000); return () => clearInterval(id); }, []);
  const ms = hasta.getTime() - Date.now();
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400e3);
  const h = Math.floor((ms % 86400e3) / 3600e3);
  const m = Math.floor((ms % 3600e3) / 60e3);
  return (
    <span className="sigg-chip">
      {titulo && <b>{titulo}</b>}
      <span className="num">{d > 0 ? `${d}d ` : ''}{String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}</span>
    </span>
  );
}

function GadgetsOverlay({ cfg, local, campana, combos }: {
  cfg: GadgetsCfg; local?: string | null; campana?: CampanaInfo | null; combos?: Combo[];
}) {
  const slots: Record<string, ReactNode[]> = { 'sup-centro': [], 'inf-izq': [], 'inf-der': [] };
  for (const [i, g] of (cfg.items ?? []).entries()) {
    const pos = slots[g.pos] ? g.pos : 'inf-izq';
    if (g.tipo === 'saludo') {
      if (campana?.saludo) slots[pos].push(<span key={i} className="sigg-chip sigg-saludo">{campana.saludo}</span>);
    } else if (g.tipo === 'local') {
      const txt = g.texto?.trim() || local;
      if (txt) slots[pos].push(<span key={i} className="sigg-chip">{txt}</span>);
    } else if (g.tipo === 'cuenta') {
      const combo = g.combo_id ? (combos ?? []).find((c) => c.id === g.combo_id) : null;
      const hasta = combo?.hasta ? new Date(`${combo.hasta}T23:59:59`) : g.fecha ? new Date(g.fecha) : null;
      if (hasta && !Number.isNaN(hasta.getTime())) {
        slots[pos].push(<CuentaMini key={i} titulo={g.titulo ?? combo?.nombre} hasta={hasta} />);
      }
    }
  }
  if (!Object.values(slots).some((s) => s.length)) return null;
  return (
    <div className="sigg-layer" aria-hidden>
      {Object.entries(slots).map(([pos, chips]) => (chips.length
        ? <div key={pos} className={`sigg-slot sigg-${pos}`}>{chips}</div>
        : null))}
    </div>
  );
}

/* Reseñas internas: opinión del paciente (iniciales) + promedio de la clínica */
function ResenaScene({ item, promedio, total }: { item: ResenaItem; promedio: number; total: number }) {
  return (
    <div className="sigp-resena">
      <div className="sigp-resena-head">
        <span className="prom">{Number(promedio).toFixed(1)}</span>
        <span className="stars" aria-hidden>{'★'.repeat(Math.round(promedio))}{'☆'.repeat(Math.max(0, 5 - Math.round(promedio)))}</span>
        <span className="tot">{total} opiniones de pacientes</span>
      </div>
      <blockquote className="sigp-resena-quote">
        <span className="qstars" aria-hidden>{'★'.repeat(item.estrellas)}</span>
        <p>“{item.comentario}”</p>
        <footer>— {item.autor} · {item.fecha}</footer>
      </blockquote>
    </div>
  );
}

/* Turnos: médicos con cupos disponibles HOY (captación de citas/teleconsulta) */
function TurnosScene({ titulo, items }: { titulo?: string | null; items: TurnoMedico[] }) {
  return (
    <div className="sigp-turnos">
      <div className="sigp-turnos-tit">{titulo || 'Médicos disponibles HOY'}</div>
      <div className="sigp-turnos-sub">Agenda tu cita o teleconsulta en recepción</div>
      <ul className="sigp-turnos-lista">
        {items.slice(0, 6).map((t, i) => (
          <li key={i}>
            <div className="quien">
              <b>{t.medico}</b>
              {t.especialidad && <em>{t.especialidad}</em>}
            </div>
            <div className="cuando">
              <span className="prox">{t.proximo}</span>
              <span className="cupos">{t.cupos} {t.cupos === 1 ? 'cupo' : 'cupos'} hoy</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Redes sociales: tarjeta de post (imagen + caption + métricas) */
const RED_META: Record<string, { nombre: string; color: string }> = {
  instagram: { nombre: 'Instagram', color: 'linear-gradient(45deg,#f09433,#dc2743,#bc1888)' },
  facebook: { nombre: 'Facebook', color: '#1877F2' },
  tiktok: { nombre: 'TikTok', color: '#151515' },
  x: { nombre: 'X', color: '#151515' },
};

function SocialScene({ post, cuenta }: { post: SocialPost; cuenta: SocialCuenta }) {
  const meta = RED_META[cuenta.red] ?? { nombre: cuenta.red, color: 'var(--accent)' };
  const fecha = post.publicado_en
    ? new Date(post.publicado_en).toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })
    : null;
  const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)} mil` : String(n));
  return (
    <div className="sigp-social">
      <div className={`sigp-social-card${post.imagen ? '' : ' solo-texto'}`}>
        {post.imagen && <div className="img"><img src={post.imagen} alt="" /></div>}
        <div className="body">
          <div className="head">
            {cuenta.avatar
              ? <img className="ava" src={cuenta.avatar} alt="" />
              : <span className="ava ini" style={{ background: meta.color }}>{(cuenta.nombre || cuenta.handle).replace(/^@/, '').charAt(0).toUpperCase()}</span>}
            <div className="who">
              <b>{cuenta.nombre || cuenta.handle}</b>
              <span>@{cuenta.handle.replace(/^@/, '')}</span>
            </div>
            <span className="net" style={{ background: meta.color }}>{meta.nombre}</span>
          </div>
          {post.texto && <p className="txt">{post.texto}</p>}
          <div className="foot">
            {post.likes != null && <span>♥ {fmt(post.likes)}</span>}
            {post.comentarios != null && <span>💬 {fmt(post.comentarios)}</span>}
            {fecha && <span className="f">{fecha}</span>}
            <span className="cta">Síguenos</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PorVencerScene({ titulo, items }: { titulo?: string | null; items: VencItem[] }) {
  return (
    <div className="sigp-venc">
      <div className="sigp-venc-tit"><span className="bang">¡Aprovecha!</span> {titulo || 'Antes de que se agote'}</div>
      <div className="sigp-venc-grid">
        {items.slice(0, 9).map((p) => {
          const urg = p.dias <= 15 ? 'urg' : p.dias <= 30 ? 'med' : '';
          return (
            <div key={`${p.id}-${p.lote ?? ''}`} className={`sigp-venc-card ${urg}`}>
              {p.imagen ? <img src={p.imagen} alt="" /> : <span className="ph"><Package /></span>}
              <div className="body">
                <div className="n">{p.nombre}{p.marca && <em> · {p.marca}</em>}</div>
                <div className={`vence ${urg}`}>Vence en {p.dias} {p.dias === 1 ? 'día' : 'días'}</div>
                <div className="p">
                  {p.precio_antes && p.precio_antes > p.precio && <span className="antes">${Number(p.precio_antes).toFixed(2)}</span>}
                  <span className="now">${Number(p.precio).toFixed(2)}</span>
                  {p.descuento > 0 && <span className="d">-{Number(p.descuento)}%</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function saludCond(temp: number, code: number, uv: number): string {
  if (uv >= 7) return 'sol';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 99)) return 'lluvia';
  if (temp <= 16) return 'frio';
  if (temp >= 29) return 'calor';
  return 'siempre';
}

function SaludScene({ titulo, salud }: { titulo?: string | null; salud: SaludData | null }) {
  const lat = salud?.lat ?? -2.19, lon = salud?.lon ?? -79.89; // fallback: Guayaquil
  const [w, setW] = useState<{ temp: number; code: number; uv: number } | null>(null);
  useEffect(() => {
    let cancel = false; const key = `sig_salud_w:${lat},${lon}`;
    (async () => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&daily=uv_index_max&timezone=auto`);
        const j = await r.json() as { current: { temperature_2m: number; weather_code: number }; daily: { uv_index_max: number[] } };
        const val = { temp: Math.round(j.current.temperature_2m), code: j.current.weather_code, uv: Math.round(j.daily.uv_index_max?.[0] ?? 0) };
        if (!cancel) { setW(val); void cacheSet(key, { val }); }
      } catch {
        const c = await cacheGet<{ val: { temp: number; code: number; uv: number } }>(key);
        if (!cancel && c?.val) setW(c.val);
      }
    })();
    return () => { cancel = true; };
  }, [lat, lon]);

  const cond = w ? saludCond(w.temp, w.code, w.uv) : 'siempre';
  const matches = useMemo(() => {
    const all = salud?.tips ?? [];
    const m = all.filter((t) => t.condicion === cond);
    const base = all.filter((t) => t.condicion === 'siempre');
    return m.length ? m : base.length ? base : all;
  }, [salud, cond]);
  const [i, setI] = useState(0);
  useEffect(() => { setI(0); const id = setInterval(() => setI((x) => x + 1), 6000); return () => clearInterval(id); }, [cond, matches.length]);
  const tip = matches.length ? matches[i % matches.length] : null;
  const wm = w ? wmo(w.code) : null;
  if (!tip) return <div className="sigp-msg"><h2>{titulo || 'Salud del día'}</h2></div>;
  return (
    <div className="sigp-salud">
      <div className="sigp-salud-tit">{titulo || 'Consejo de salud del día'}</div>
      <div className="sigp-salud-tip"><span className="ic">{tip.icono || '💧'}</span><p>{tip.texto}</p></div>
      {w && wm && (
        <div className="sigp-salud-clima">
          <span className="wic">{wm.icon}</span><span className="t">{w.temp}°</span>
          <span className="uv">UV {w.uv}</span><span className="lbl">{wm.label}</span>
        </div>
      )}
      {tip.producto && (
        <div className="sigp-salud-prod">
          {tip.producto.imagen ? <img src={tip.producto.imagen} alt="" /> : <span className="ph"><Package /></span>}
          <div className="body">
            <div className="cta">{tip.cta || 'Recomendado hoy'}</div>
            <div className="n">{tip.producto.nombre}{tip.producto.marca && <em> · {tip.producto.marca}</em>}</div>
            <div className="p">
              {tip.producto.precio_antes && tip.producto.precio_antes > tip.producto.precio && <span className="antes">${Number(tip.producto.precio_antes).toFixed(2)}</span>}
              <span className="now">${Number(tip.producto.precio).toFixed(2)}</span>
              {tip.producto.descuento > 0 && <span className="d">-{Number(tip.producto.descuento)}%</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CrossSellScene({ token, titulo, fallback }: { token?: string; titulo?: string | null; fallback: Item[] }) {
  const [reco, setReco] = useState<Reco[] | null>(null);
  useEffect(() => {
    if (!token) { setReco(null); return; }
    let cancel = false;
    const tick = async () => {
      try {
        const r = (await apiClient.post('/rpc/sig_contexto', { _token: token })).data as { activo: boolean; productos: Reco[] };
        if (!cancel) setReco(r?.activo ? r.productos : []);
      } catch { if (!cancel) setReco([]); }
    };
    void tick();
    const id = setInterval(() => void tick(), 4000);
    return () => { cancel = true; clearInterval(id); };
  }, [token]);
  const enVivo = (reco?.length ?? 0) > 0;
  const lista: Reco[] = enVivo ? reco! : fallback.slice(0, 6).map((p) => ({ nombre: p.nombre, marca: p.marca, precio: Number(p.precio), descuento: p.descuento ?? 0 }));
  return (
    <div className="sigp-cross">
      <div className="sigp-cross-tit">{titulo || (enVivo ? 'También te puede interesar' : 'Lo más vendido')}</div>
      <div className="sigp-cross-grid">
        {lista.map((p, i) => (
          <div key={i} className="sigp-cross-card">
            <div className="n">{p.nombre}</div>
            {p.marca && <div className="m">{p.marca}</div>}
            <div className="p">${Number(p.precio).toFixed(2)}{(p.descuento ?? 0) > 0 && <span className="d">-{p.descuento}%</span>}</div>
          </div>
        ))}
        {!lista.length && <div className="sigp-feed-fecha">Sin sugerencias por ahora</div>}
      </div>
    </div>
  );
}

function FeedScene({ feed, titulo, lat, lon, fecha, fuenteId, feeds }: { feed: string; titulo?: string | null; lat?: number; lon?: number; fecha?: string | null; fuenteId?: string; feeds?: FeedFuente[] }) {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setAhora(new Date()), 1000); return () => clearInterval(id); }, []);
  if (feed === 'clima') return <ClimaFeed titulo={titulo} lat={lat} lon={lon} />;
  if (feed === 'noticias') {
    const fuente = (feeds ?? []).find((f) => f.id === fuenteId) ?? feeds?.[0];
    const items = fuente?.items ?? [];
    return (
      <div className="sigp-feed noticias">
        <div className="sigp-feed-tit">{titulo || fuente?.nombre || 'Noticias'}</div>
        {items.length ? (
          <ul className="sigp-noticias">{items.slice(0, 6).map((it, i) => <li key={i}><span className="b" />{it.titulo}</li>)}</ul>
        ) : <div className="sigp-feed-fecha">Sin titulares disponibles</div>}
      </div>
    );
  }
  if (feed === 'cuenta') {
    const target = fecha ? new Date(fecha).getTime() : ahora.getTime();
    const diff = Math.max(0, target - ahora.getTime());
    const d = Math.floor(diff / 86400000), h = Math.floor(diff / 3600000) % 24, m = Math.floor(diff / 60000) % 60, sec = Math.floor(diff / 1000) % 60;
    return (
      <div className="sigp-feed cuenta">
        {titulo && <div className="sigp-feed-tit">{titulo}</div>}
        <div className="sigp-feed-cd">
          {[['días', d], ['hs', h], ['min', m], ['seg', sec]].map(([l, v]) => <span key={l as string}><b>{String(v).padStart(2, '0')}</b>{l}</span>)}
        </div>
      </div>
    );
  }
  return (
    <div className="sigp-feed reloj">
      <div className="sigp-feed-hora">{ahora.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}</div>
      <div className="sigp-feed-fecha">{ahora.toLocaleDateString(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' })}</div>
      {titulo && <div className="sigp-feed-tit">{titulo}</div>}
    </div>
  );
}

function StockLinea({ p, kind }: { p: Item; kind: string }) {
  if (kind === 'out') return <div className="sigp-stock out"><span className="d" />Agotado</div>;
  if (kind === 'low') {
    const s = p.stock ?? 0;
    return (
      <div className="sigp-stock low"><span className="d" />Últimas {s}
        <span className="sigp-meter">{[0, 1, 2].map((i) => <i key={i} className={i < s ? '' : 'off'} />)}</span>
      </div>
    );
  }
  return <div className="sigp-stock ok"><span className="d" />Disponible</div>;
}
